import "./ApplicationItem.module.scss";
import { useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  AutoComplete,
  Badge,
  Button,
  Checkbox,
  Drawer,
  Collapse,
  DatePicker,
  Divider,
  Dropdown,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popover,
  Result,
  Select,
  Table,
  TableProps,
  Tag,
  Typography,
  Tooltip,
  Radio,
  Spin,
  Card,
  Space,
  List,
  Switch,
} from "antd";
import { useForm } from "antd/es/form/Form";
import { ApiService } from "api/api.service";

import RichTextEditor from "components/RichTextEditor";
import {
  TbAlertCircle,
  TbAlertTriangle,
  TbArrowDown,
  TbArrowUp,
  TbAutomation,
  TbCaretDownFilled,
  TbChevronDown,
  TbChevronLeft,
  TbChevronRight,
  TbCopy,
  TbCreditCard,
  TbEyeDollar,
  TbFileTypePdf,
  TbInfoCircle,
  TbLock,
  TbMinus,
  TbPaperclip,
  TbPdf,
  TbPlus,
  TbRefresh,
  TbSquareChevronLeftFilled,
  TbSquareChevronRightFilled,
  TbTrash,
  TbX,
} from "react-icons/tb";

import {
  calculateAPR,
  formatBytes,
  formatDate,
  formatNumber,
  formatPercent,
  getContentTypeFromFile,
} from "utils/functions";
import { loader, loaderSm, SingleQueryWrapper } from "components/QueryWrapper";
import TextNumeric from "components/TextNumeric";
import { T_Populate } from "types/_utils.types";
import {
  APPLICATION_STATUS,
  ApplicationDto,
  ApplicationSchemaDto,
  BusinessInfo,
  ClientDetails,
  GetClearApplicationDto,
  SelectedBank,
  StrategyType,
  T_CustomStrategy,
} from "dtos/application.dto";
import { FileSchemaDto } from "dtos/file.dto";
import {
  DISBURSEMENT_STATUS,
  LEDGER_CATEGORY,
  LedgerSchemaDto,
  LOCK_STATUS,
  TRANSACTION_STATUS,
} from "dtos/ledger.dto";

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  TbCircleCheckFilled,
  TbDotsVertical,
  TbEdit,
  TbExternalLink,
  TbFile,
  TbInfoCircleFilled,
  TbNotebook,
} from "react-icons/tb";
import {
  CHART_COLORS,
  DAY_OPTIONS,
  getLedgerStatusColors,
  stateNames,
  WEEKLY_PAYMENTS_REDUCTION_MULTIPLIER,
} from "utils/constants";
import { formatDateTime, formatMoney, toTitleCase } from "utils/functions";
import { queryClient } from "utils/queryClient";
import { storedTokens } from "utils/storedTokens";
import { FREQUENCY_TYPES, LOAN_TYPES, LoanSchemaDto } from "dtos/loan.dto";
import { BankSchemaDto, BankStatusType, TransactionType } from "dtos/bank.dto";
import dayjs from "dayjs";
import { number, set } from "zod";
import { LawSuitTypes } from "dtos/lawsuit.dto";
// import { ApplicationSchemaDto } from "types/application.types";
import { Strategy } from "api/onboarding.service";
import { AppCard } from "components/AppCard";
import {
  ReOriginalOptimalBarChart,
  LineChartFilled,
  ReAmountHorizontalBarChart,
  ReAmountVerticalBarChart,
  ReProfitVerticalBarChart,
  WeeklyDistributionPieChart,
  WithdrawalPercentageBarChart,
  ReNegativeDays,
  ReNsfTransactions,
  BarChart,
} from "components/Charts";
import StackedLineChart from "components/StackedLineChart";
import LoanPieChart from "pages/Onboarding/components/LoanPieChart";
import { LawSuitSchemaDto } from "dtos/lawsuit.dto";
import { T_QueryData } from "types/_utils.types";
import { ColumnsType } from "antd/es/table";
import { IoArrowBack } from "react-icons/io5";
import { CheckCircleOutlined, InfoCircleOutlined, WalletOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { FiCalendar, FiStopCircle } from "react-icons/fi";
import { ModifyPostingDayModal } from "components/ModifyPostingDayModal";
import { LoanFrequencyMultiplier } from "components/Application/constants";
import { MultiCreditorMonthlyWithhold } from "components/APRCharts/APRCharts";
import { MonthlyBreakdownChart } from "components/Application/MonthlyBreakdownChart";
import { MonthlyBreakdownTable } from "components/APRCharts/CreditorBreakDownTable";
import { StartPaymentAutomationModal } from "components/PaymentJobs/StartPaymentAutomationModal";
// import { BarChart } from "recharts";

const { Paragraph, Text, Title } = Typography;

function redirectToFileViewer(
  fileData: NonNullable<ReturnType<typeof ApiService.file.useFetchFileUrl>["data"]>["result"],
) {
  storedTokens.fileViewer.setToken(fileData.link);
  window.open(`/file/${encodeURIComponent(fileData.key)}`, "_blank");
}

export function ViewFileBtn({ fileId, appKey }: { fileId: string; appKey: string }) {
  const getFileUrl = ApiService.file.useFetchFileUrl();

  return (
    <Button
      variant="outlined"
      color="primary"
      size="small"
      icon={<TbFile />}
      loading={getFileUrl.isPending}
      onClick={() => {
        getFileUrl.mutate(
          { fileId, appKey },
          {
            onSuccess: (data) => {
              redirectToFileViewer(data.result);
            },
          },
        );
      }}
    >
      View File
    </Button>
  );
}

export function ViewFileLink({
  fileId,
  appKey,
  children,
}: {
  fileId: string;
  appKey: string;
  children: React.ReactNode;
}) {
  const getFileUrl = ApiService.file.useFetchFileUrl();

  return (
    <span
      className="cursor-pointer text-purple-600 line-clamp-2"
      onClick={() => {
        getFileUrl.mutate(
          { fileId, appKey },
          {
            onSuccess: (data) => {
              redirectToFileViewer(data.result);
            },
          },
        );
      }}
    >
      {children}
      <TbExternalLink className="inline align-baseline ml-1 mb-1" size={14} />
    </span>
  );
}

export function ViewFileBanks({ attachments, appKey }: { attachments: FileSchemaDto[]; appKey: string }) {
  const getFileUrl = ApiService.file.useFetchFileUrl();

  return (
    <Dropdown
      trigger={["click"]}
      menu={{
        items: attachments?.map((el) => ({
          label: (
            <div>
              <div className="flex items-center gap-1">
                <TbFileTypePdf />
                <Typography.Text ellipsis={{ tooltip: el.original_file_name }} className="!max-w-[300px]">
                  {el.original_file_name}
                </Typography.Text>
              </div>
            </div>
          ),
          key: el._id,
        })),
        onClick: ({ key }) => {
          getFileUrl.mutate(
            { fileId: key, appKey },
            {
              onSuccess: (data) => {
                redirectToFileViewer(data.result);
              },
            },
          );
        },
      }}
    >
      <Button variant="outlined" color="primary" size="small" icon={<TbFile />} loading={getFileUrl.isPending}>
        View Files
      </Button>
    </Dropdown>
  );
}

export function ViewFileLawsuits({
  attachments,
  appKey,
  onDelete,
  deletingFileId,
}: {
  attachments: FileSchemaDto[];
  appKey: string;
  onDelete?: (fileId: string) => void;
  deletingFileId?: string | null;
}) {
  const getFileUrl = ApiService.file.useFetchFileUrl();

  const handleOpen = (fileId: string) => {
    if (deletingFileId) return;
    getFileUrl.mutate(
      { fileId, appKey },
      {
        onSuccess: (data) => {
          redirectToFileViewer(data.result);
        },
      },
    );
  };

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      overlayInnerStyle={{ padding: 4 }}
      content={
        <List
          style={{ width: 320 }}
          size="small"
          dataSource={attachments}
          renderItem={(el) => {
            const isDeleting = deletingFileId === el._id;
            const isLocked = !!deletingFileId;

            return (
              <List.Item
                key={el._id}
                className={`!px-2 !py-2 rounded-md ${isLocked ? "" : "cursor-pointer hover:bg-gray-50"}`}
                style={{ opacity: isLocked && !isDeleting ? 0.5 : 1 }}
                onClick={() => handleOpen(el._id)}
                actions={[
                  <Button
                    key="delete"
                    type="text"
                    danger
                    size="small"
                    icon={<TbTrash size={14} />}
                    loading={isDeleting}
                    disabled={isLocked && !isDeleting}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete?.(el._id);
                    }}
                  />,
                ]}
              >
                <List.Item.Meta
                  avatar={<TbFileTypePdf size={18} className="text-purple-600" />}
                  title={
                    <div className="flex items-center gap-1.5">
                      <Typography.Text ellipsis={{ tooltip: el.original_file_name }} className="!max-w-[200px] !mb-0">
                        {el.original_file_name}
                      </Typography.Text>
                      <TbExternalLink className="shrink-0 text-gray-400" size={13} />
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      }
    >
      <Button variant="outlined" color="primary" size="small" icon={<TbFile />} loading={getFileUrl.isPending}>
        View Files
      </Button>
    </Popover>
  );
}

type T_FullApplication = NonNullable<ReturnType<typeof ApiService.application.useGetByAppKey>["data"]>;
const CREDITOR_MIN_FEE = 300;
export const PaymentModal = ({
  openPaymentModal,
  handleOpenPaymentModal,
  paymentTable,
  fullApplication,
  refetchPaymentTable,
}: {
  paymentTable: ApplicationDto["payment_table"];
  openPaymentModal: boolean;
  handleOpenPaymentModal: () => void;
  fullApplication: T_FullApplication;
  refetchPaymentTable: () => void;
}) => {
  const adminApplication = fullApplication?.application;
  const getclearApplication = fullApplication?.data?.application;
  const tenure = getclearApplication?.strategy?.tenure;
  const maxProgramFee = Math.round(
    (getclearApplication?.strategy?.weekly_payments ?? 0) - 25 - 36.25 - CREDITOR_MIN_FEE,
  );
  const latestPaymentTable = paymentTable?.[paymentTable?.length - 1];

  const [programFee, setProgramFee] = useState(latestPaymentTable?.repeat_rows?.[0]?.row?.program_fee ?? 0);
  const [totalWeeks, setTotalWeeks] = useState(latestPaymentTable?.repeat_rows?.[0]?.count ?? 0);
  const [totalProgramFee] = useState(programFee * totalWeeks);
  const [text, setText] = useState<string | null>(null);
  const [isApplyButtonClicked, setIsApplyButtonClicked] = useState(false);
  const [isSaved, setIsSaved] = useState(true);
  const updateLedger = ApiService.application.useUpdateLedger();
  const executeActum = ApiService.application.useUpdateLedger();

  const handleApplyChanges = () => {
    const prevProgramFee = programFee;
    const calculatedWeeks = totalProgramFee / programFee;
    // Round down to nearest integer
    const roundedWeeks = Math.floor(calculatedWeeks);

    // Recalculate program fee to maintain exact total with rounded weeks
    const adjustedProgramFee = totalProgramFee / roundedWeeks;

    let roundedOff = Number(adjustedProgramFee.toFixed(2));
    if (roundedOff > maxProgramFee) {
      roundedOff = maxProgramFee;
    }
    setText(`* Your quoted price rounded off from ${formatMoney(prevProgramFee)} to ${formatMoney(roundedOff)}`);
    setProgramFee(roundedOff);
    setTotalWeeks(roundedWeeks);
    setIsApplyButtonClicked(true);
    setIsSaved(false);
  };

  const handleUpdateLedger = () => {
    updateLedger.mutate(
      {
        application_id: adminApplication?._id,
        new_program_fee: programFee,
        new_weeks: totalWeeks, // Todo: change if in future weeks can also be changed
      },
      {
        onSuccess: () => {
          setIsSaved(true);
          message.success("Ledger modified successfully");
        },
      },
    );
    setIsApplyButtonClicked(false);
  };

  const handleModalClose = () => {
    refetchPaymentTable();
    handleOpenPaymentModal();
  };

  return (
    <Modal width={750} footer={null} open={openPaymentModal} onCancel={handleModalClose} title="Modify ledger">
      {latestPaymentTable?.created ? (
        <Badge.Ribbon color="blue" text={`v${latestPaymentTable?.version}`}>
          <Alert
            className="my-3"
            type="info"
            showIcon
            icon={<TbInfoCircleFilled />}
            message={
              <div>
                <Typography.Text type="secondary" className="text-xs">
                  Last updated at {formatDateTime(latestPaymentTable?.created)}
                </Typography.Text>
                <Typography.Paragraph className="!mb-0 mt-1">
                  <span className="font-medium">Reason: </span>
                  {latestPaymentTable?.update_purpose_code}
                </Typography.Paragraph>
              </div>
            }
          />
        </Badge.Ribbon>
      ) : null}
      <Table
        columns={[
          {
            key: "program_fee",
            title: "Program Fee",
            render: (_, data) => {
              return (
                <InputNumber
                  prefix="$"
                  style={{ width: "100%" }}
                  value={programFee}
                  min={Math.ceil(totalProgramFee / (tenure ?? 0))}
                  max={maxProgramFee}
                  onChange={(value) => setProgramFee(value as number)}
                />
              );
            },
          },
          {
            key: "total_weeks",
            title: "Total Weeks",
            render: (_, data) => {
              return <TextNumeric>{data?.total_weeks}</TextNumeric>;
            },
          },
          {
            key: "total_program_fee",
            title: "Total Program Fee",
            render: (_, data) => {
              // return <InputNumber style={{ width: "100%" }} disabled value={data.total_program_fee} />;
              return <TextNumeric>{formatMoney(data?.total_program_fee)}</TextNumeric>;
            },
          },
          {
            key: "action",
            title: "Action",
            render: () => {
              return (
                <Button onClick={handleApplyChanges} type="primary">
                  Apply Changes
                </Button>
              );
            },
          },
        ]}
        dataSource={[{ program_fee: programFee, total_weeks: totalWeeks, total_program_fee: totalProgramFee }]}
        pagination={false}
      />
      <Typography.Paragraph className="mt-2">{text}</Typography.Paragraph>
      <div className="flex justify-end gap-4 mt-4">
        <Button
          onClick={handleUpdateLedger}
          loading={updateLedger.isPending}
          disabled={!isApplyButtonClicked}
          type="primary"
        >
          Save
        </Button>
        <Button
          disabled={!isSaved}
          type="primary"
          onClick={() => {
            executeActum.mutate(
              {
                application_id: adminApplication?._id,
                new_program_fee: programFee,
                new_weeks: totalWeeks, // Todo: change if in future weeks can also be changed
              },
              {
                onSuccess: () => {
                  message.success("Actum execution initiated");
                  handleModalClose();
                },
              },
            );
          }}
        >
          Execute
        </Button>
      </div>
    </Modal>
  );
};

export function MarkPaymentModal({
  dto,
  clear,
  applicationId,
}: {
  dto: LedgerSchemaDto;
  clear: () => void;
  applicationId: string;
}) {
  const [form] = useForm<{
    mode?: "program_fee" | "service_fee" | "legal_fee" | "creditor_total" | "creditors" | "budget";
    program_fee?: number;
    service_fee?: number;
    legal_fee?: number;
    creditor_total?: number;
    creditors?: { amount?: number; ref_transaction_id?: string; posting_date?: Date }[];
    budget?: number;
    ref_transaction_id?: string;
    posting_date?: Date;
    fully_paid_ref_transaction_id?: string;
    fully_paid_posting_date?: Date;
  }>();
  const [settlementMode, setSettlementMode] = useState<"manual" | "automation">("manual");

  const markPayment = ApiService.ledger.useMarkPaymentManually();
  const markFullyPaid = ApiService.ledger.useMarkFullyPaid();

  const { data: balanceRes, isLoading, isError } = ApiService.ledger.useGetBalance(dto?._id);

  // const [paidFully, setPaidFully] = useState(false);
  const [allUndefined, setAllUndefined] = useState(true);
  const [selectedCreditorId, setSelectedCreditorId] = useState<string | undefined>(undefined);
  const [fullSettleOpen, setFullSettleOpen] = useState(false);
  const [fullSettleLoading, setFullSettleLoading] = useState(false);
  const [partialSettleOpen, setPartialSettleOpen] = useState(false);
  const [partialSettleLoading, setPartialSettleLoading] = useState(false);
  const settlementModeRef = useRef<"manual" | "automation">("manual");

  const isLockedOrPaid = dto.status === TRANSACTION_STATUS.PAID || dto.lock === LOCK_STATUS.LOCKED;

  const round2 = (num: number) => Number(num.toFixed(2));

  const balanceError = isError;

  const currentBudget = balanceError ? 0 : round2(balanceRes?.result?.balance ?? 0);

  const mode = Form.useWatch("mode", form);

  // const totalRemaining = round2(
  //   ["program_fee", "service_fee", "legal_fee", "creditor_total"].reduce((acc, field) => {
  //     const total = dto.distribution[field as keyof typeof dto.distribution] ?? 0;

  //     const paid = dto.payment_statuses?.[field as keyof typeof dto.payment_statuses] ?? 0;

  //     return acc + (total - paid);
  //   }, 0),
  // );

  const totalRemaining = round2(
    ["program_fee", "service_fee", "legal_fee", "creditor_total"].reduce((acc, field) => {
      const total =
        field === "creditor_total"
          ? Math.max(dto.distribution.creditor_total ?? 0, dto.distribution.final_creditor_total ?? 0)
          : (dto.distribution[field as keyof typeof dto.distribution] ?? 0);
      const paid = dto.payment_statuses?.[field as keyof typeof dto.payment_statuses] ?? 0;
      return acc + (total - paid);
    }, 0),
  );

  const isFullySettled = totalRemaining <= 0;

  const maxBudgetTopup = round2(Math.max(0, totalRemaining - currentBudget));

  const isBudgetAlreadyFull = maxBudgetTopup <= 0;
  const isWalletInsufficient = currentBudget < totalRemaining;

  const disableModes = isLockedOrPaid || balanceError || isFullySettled || currentBudget <= 0;

  const disableBudgetMode = isLockedOrPaid || isFullySettled || isBudgetAlreadyFull;

  // const disablePaidFully = isLockedOrPaid || isFullySettled || mode === "budget" || mode === "creditors";

  // useEffect(() => {
  //   const prevMode = form.getFieldValue("mode");

  //   if (paidFully) {
  //     if (prevMode === "budget") {
  //       form.setFieldsValue({ budget: maxBudgetTopup });
  //       setAllUndefined(maxBudgetTopup <= 0);
  //       return;
  //     }

  //     const baseValues = Object.fromEntries(
  //       Object.entries(dto.distribution).map(([key, value]) => [
  //         key,
  //         round2(value - (dto.payment_statuses?.[key as keyof typeof dto.payment_statuses] ?? 0)),
  //       ]),
  //     );

  //     form.setFieldsValue({
  //       ...baseValues,
  //       mode: prevMode || "budget",
  //     });

  //     setAllUndefined(false);
  //   } else {
  //     form.resetFields();

  //     form.setFieldsValue({
  //       mode: prevMode || "budget",
  //     });

  //     setAllUndefined(true);
  //   }
  // }, [paidFully, dto]);

  useEffect(() => {
    // if (mode === "budget") {
    //   setPaidFully(false);
    //   form.resetFields(["budget"]);
    //   setAllUndefined(true);
    // }

    if (mode !== "creditors") {
      setSelectedCreditorId(undefined);
      form.resetFields(["creditors"]);
    }
  }, [mode]);

  if (isLoading) return <Spin />;

  const getBalanceStyles = (amount: number) => {
    if (amount <= 0) {
      return {
        bg: "bg-red-50 border-red-200",
        text: "text-red-600",
        label: "text-red-700",
      };
    }

    if (amount < 100) {
      return {
        bg: "bg-yellow-50 border-yellow-200",
        text: "text-yellow-600",
        label: "text-yellow-700",
      };
    }

    return {
      bg: "bg-green-50 border-green-200",
      text: "text-green-600",
      label: "text-green-700",
    };
  };

  const styles = getBalanceStyles(currentBudget);

  const options = [
    { label: "Wallet Topup", value: "budget", disabled: disableBudgetMode },
    { label: "Program Fee", value: "program_fee", disabled: disableModes },
    { label: "Service Fee", value: "service_fee", disabled: disableModes },
    { label: "Legal Fee", value: "legal_fee", disabled: disableModes },
    { label: "Specific Creditors", value: "creditors", disabled: disableModes },
  ];

  return (
    <Modal
      title="Mark Ledger Row"
      open={!!dto}
      onCancel={clear}
      destroyOnHidden={false}
      afterOpenChange={(open) => {
        if (!open) {
          form.resetFields();
          // setPaidFully(false);
        }
      }}
      okButtonProps={{
        disabled: allUndefined || markPayment.isPending || isLockedOrPaid || isFullySettled,
        loading: markPayment.isPending,
      }}
      okText="Submit"
      onOk={() => {
        form.validateFields().then((values) => {
          const { mode } = values;
          if (!mode) return;

          let amount = 0;
          if (mode === "creditors") {
            const selectedIdx = dto.creditors.findIndex((c) => c.creditor_id === selectedCreditorId);
            amount = values.creditors?.[selectedIdx]?.amount ?? 0;
          } else if (mode !== "budget") {
            amount = values[mode] ?? 0;
          }

          const wouldFullySettle = amount >= totalRemaining;

          if (wouldFullySettle) {
            setPartialSettleOpen(true);
          } else {
            form.submit();
          }
        });
      }}
      centered
      width={800}
      styles={{
        body: {
          maxHeight: 650,
          overflowY: "auto",
          padding: "20px 24px",
        },
        content: {
          paddingInline: 28,
        },
      }}
    >
      {isLockedOrPaid && <Alert type="success" showIcon message="Ledger row has been locked." className="mb-3" />}

      {balanceError && (
        <Alert
          type="warning"
          showIcon
          message="Unable to fetch wallet balance. Actions are restricted."
          className="mb-3"
        />
      )}
      {isFullySettled && (
        <Alert
          type="success"
          showIcon
          message="All distributions are fully paid. No action required."
          className="mb-3"
        />
      )}
      <Card title="Distribution Status" className="mb-4 rounded-xl border bg-gray-50">
        {/* <Typography.Text type="secondary">Distribution Status</Typography.Text> */}

        <div className="mt-2 space-y-1">
          {["program_fee", "service_fee", "legal_fee", "creditor_total"].map((field) => {
            const total = round2(
              field === "creditor_total"
                ? Math.max(dto.distribution.creditor_total ?? 0, dto.distribution.final_creditor_total ?? 0)
                : (dto.distribution[field as keyof typeof dto.distribution] ?? 0),
            );

            const paid = round2(dto.payment_statuses?.[field as keyof typeof dto.payment_statuses] ?? 0);

            const remaining = round2(total - paid);

            return (
              <div key={field} className="flex justify-between text-sm">
                <span className="capitalize">{field.replace("_", " ")}</span>

                <span>
                  <span className="line-through text-gray-400 mr-2">${total}</span>
                  <span className="font-semibold">${remaining}</span>
                </span>
              </div>
            );
          })}
        </div>

        <div className="mt-3 flex justify-between font-semibold">
          <span>Total Remaining</span>
          <span>${totalRemaining}</span>
        </div>
      </Card>

      <div
        className={`mb-4 p-4 rounded-xl border flex justify-between items-center shadow-sm transition-all duration-300 ${styles.bg}`}
      >
        <div className={`flex items-center gap-2 ${styles.label}`}>
          <WalletOutlined />
          <span className="text-sm font-medium">Application Wallet</span>
        </div>

        <span className={`text-lg font-semibold ${styles.text}`}>${currentBudget}</span>
      </div>
      <Form
        layout="vertical"
        form={form}
        requiredMark={false}
        onFinish={(values) => {
          const { mode, ref_transaction_id, posting_date } = values;

          const payload: any = {
            idx: dto.category === LEDGER_CATEGORY.MONTHLY ? dto.week * 4 : dto.week,
            application_id: applicationId,
          };

          if (mode === "creditors") {
            const selectedIdx = dto.creditors.findIndex((c) => c.creditor_id === selectedCreditorId);

            const creditorFormValue = selectedIdx !== -1 ? values.creditors?.[selectedIdx] : undefined;

            payload.creditor =
              creditorFormValue && typeof creditorFormValue.amount === "number" && creditorFormValue.amount > 0
                ? {
                    creditor_id: selectedCreditorId,
                    amount: creditorFormValue.amount,
                    ref_transaction_id: creditorFormValue.ref_transaction_id,
                    posting_date: creditorFormValue.posting_date
                      ? ((creditorFormValue.posting_date as any).toDate?.() ??
                        new Date(creditorFormValue.posting_date as any))
                      : undefined,
                  }
                : undefined;
          } else if (mode === "budget") {
            payload.budget = values.budget;
            payload.ref_transaction_id = ref_transaction_id;
            payload.posting_date = posting_date
              ? ((posting_date as any).toDate?.() ?? new Date(posting_date as any))
              : undefined;
          } else if (mode) {
            payload[mode] = values[mode];
            payload.ref_transaction_id = ref_transaction_id;
            payload.posting_date = posting_date
              ? ((posting_date as any).toDate?.() ?? new Date(posting_date as any))
              : undefined;
          }

          payload.settlementType = settlementModeRef.current;

          markPayment.mutate(
            { body: payload },
            {
              onSuccess: (data) => {
                message.success(data.message);
                // setPaidFully(false);
                form.resetFields();
                queryClient.invalidateQueries({
                  queryKey: ["ledger/budget", dto._id],
                });
                queryClient.invalidateQueries({
                  queryKey: ["application/payment-table", applicationId],
                });
                queryClient.invalidateQueries({
                  queryKey: ["application", "wallet/balance", applicationId],
                });
                queryClient.invalidateQueries({
                  queryKey: ["application"],
                });

                setSettlementMode("manual");
                clear();
              },
            },
          );
        }}
        // onValuesChange={() => {
        //   let valid = !!mode;

        //   if (mode !== "budget") {
        //     valid = true;
        //   } else if (mode === "budget") {
        //     const val = form.getFieldValue("budget");
        //     valid = valid && typeof val === "number" && val >= 1 && val <= maxBudgetTopup;
        //   } else if (mode === "creditors") {
        //     const selectedIdx = dto.creditors.findIndex((c) => c.creditor_id === selectedCreditorId);
        //     const val = selectedIdx !== -1 ? form.getFieldValue(["creditors", selectedIdx, "amount"]) : undefined;
        //     valid = valid && !!selectedCreditorId && typeof val === "number" && val >= 1 && val <= currentBudget;
        //   } else if (mode) {
        //     const val = form.getFieldValue(mode);
        //     valid = valid && typeof val === "number" && val >= 1 && val <= currentBudget;
        //   }

        //   setAllUndefined(!valid);
        // }}
        onValuesChange={() => {
          let valid = !!mode;

          if (mode === "budget") {
            const val = form.getFieldValue("budget");
            valid = valid && typeof val === "number" && val >= 1 && val <= maxBudgetTopup;
          } else if (mode === "creditors") {
            const selectedIdx = dto.creditors.findIndex((c) => c.creditor_id === selectedCreditorId);
            const val = selectedIdx !== -1 ? form.getFieldValue(["creditors", selectedIdx, "amount"]) : undefined;
            valid = valid && !!selectedCreditorId && typeof val === "number" && val >= 1 && val <= currentBudget;
          } else if (mode) {
            const val = form.getFieldValue(mode);
            valid = valid && typeof val === "number" && val >= 1 && val <= currentBudget;
          }

          setAllUndefined(!valid);
        }}
      >
        {/* <div
          onClick={() => {
            if (!disablePaidFully) setPaidFully(!paidFully);
          }}
          className={`mb-4 p-4 rounded-xl border cursor-pointer transition-all duration-200 flex items-center justify-between
    ${disablePaidFully ? "opacity-50 cursor-not-allowed" : ""}
    ${paidFully ? "bg-green-50 border-green-300 shadow-sm" : "bg-gray-50 border-gray-200 hover:border-gray-300"}
  `}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-5 h-5 rounded-full border flex items-center justify-center transition-all
        ${paidFully ? "bg-green-500 border-green-500" : "border-gray-400"}
      `}
            >
              {paidFully && <div className="w-2.5 h-2.5 bg-white rounded-full" />}
            </div>

            <div>
              <div className={`text-sm font-medium ${paidFully ? "text-green-700" : "text-gray-700"}`}>
                Mark as Fully Paid
              </div>
              <div className="text-xs text-gray-500">Automatically fills remaining balances</div>
            </div>
          </div>

          {paidFully && <span className="text-xs font-semibold text-green-600">ACTIVE</span>}
        </div> */}

        <div
          className={`mb-4 rounded-xl border transition-all duration-200 overflow-hidden
  ${isLockedOrPaid || isFullySettled ? "border-gray-200 opacity-50" : "border-green-200"}
`}
        >
          {/* Top clickable row */}
          <div className="flex items-center justify-between px-4 py-3 bg-green-50">
            <div className="flex items-center gap-2">
              <CheckCircleOutlined className={isLockedOrPaid || isFullySettled ? "text-gray-300" : "text-green-500"} />
              <span
                className={`text-sm font-semibold ${isLockedOrPaid || isFullySettled ? "text-gray-400" : "text-green-700"}`}
              >
                Mark as Fully Paid
              </span>
            </div>
            <Button
              size="small"
              type="primary"
              disabled={isLockedOrPaid || isFullySettled || isWalletInsufficient}
              loading={markFullyPaid.isPending}
              onClick={() => setFullSettleOpen(true)}
            >
              {isWalletInsufficient ? "Not enough in wallet." : "Settle Fully"}
            </Button>
          </div>

          {/* Ref/date strip */}
          {!isLockedOrPaid && !isFullySettled && (
            <div className="px-4 py-3 bg-green-50 border-t border-green-100 grid grid-cols-2 gap-3">
              <Form.Item
                name="fully_paid_ref_transaction_id"
                label={<span className="text-xs text-gray-500">Reference ID</span>}
                className="mb-0"
              >
                <Input placeholder="Optional" size="small" className="rounded-md" />
              </Form.Item>

              <Form.Item
                name="fully_paid_posting_date"
                label={<span className="text-xs text-gray-500">Posting Date</span>}
                className="mb-0"
              >
                <DatePicker size="small" style={{ width: "100%" }} className="rounded-md" />
              </Form.Item>
            </div>
          )}
        </div>

        {/* {isWalletInsufficient && !isLockedOrPaid && !isFullySettled && (
          <div className="mb-4 text-xs text-yellow-600">
            Wallet balance (${currentBudget}) is insufficient to cover the total remaining (${totalRemaining}).
          </div>
        )} */}

        <Form.Item name="mode" label="Select Payment Type" rules={[{ required: true }]}>
          <div className="grid grid-cols-2 gap-3">
            {options.map((opt) => {
              const active = mode === opt.value;

              return (
                <div
                  key={opt.value}
                  onClick={() => {
                    if (!opt.disabled) form.setFieldValue("mode", opt.value);
                  }}
                  className={`
            p-3 rounded-xl border cursor-pointer transition-all duration-200
            ${opt.disabled ? "opacity-50 cursor-not-allowed" : ""}
            ${active ? "border-blue-500 bg-blue-50 shadow-sm" : "border-gray-200 hover:border-gray-300 bg-white"}
          `}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-medium ${active ? "text-blue-700" : "text-gray-700"}`}>
                      {opt.label}
                    </span>

                    {active && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                  </div>
                </div>
              );
            })}
          </div>
        </Form.Item>

        <div>
          {["program_fee", "legal_fee", "service_fee" /* "creditor_total" */].map((field) => {
            if (mode !== field) return null;

            const remaining = round2(
              (dto.distribution[field as keyof typeof dto.distribution] ?? 0) -
                (dto.payment_statuses?.[field as keyof typeof dto.payment_statuses] ?? 0),
            );

            return (
              <div key={field} className="p-3 border rounded-lg bg-blue-50">
                <Form.Item
                  name={field}
                  label={`${field.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())} (remaining $${remaining})`}
                  rules={[
                    {
                      validator: (_, val) => {
                        if (val === undefined || val === null) return Promise.resolve();
                        if (remaining <= 0) return Promise.reject(new Error("This field is already fully paid."));
                        if (val < 1) return Promise.reject(new Error("Amount must be at least $1."));
                        if (val > remaining)
                          return Promise.reject(
                            new Error(`Amount cannot exceed the remaining balance ($${remaining}).`),
                          );
                        if (val > currentBudget)
                          return Promise.reject(
                            new Error(`Amount cannot exceed the wallet balance ($${currentBudget}).`),
                          );
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  {/* <InputNumber disabled={disableModes} style={{ width: "100%" }} step={0.01} precision={2} /> */}
                  <InputNumber
                    disabled={disableModes}
                    style={{ width: "100%" }}
                    step={0.01}
                    precision={2}
                    addonAfter={
                      <span
                        className={`text-xs font-semibold px-1 ${disableModes ? "text-gray-300 cursor-not-allowed" : "text-blue-500 hover:text-blue-700 cursor-pointer"}`}
                        onClick={() => {
                          if (disableModes) return;
                          form.setFieldValue(field, Math.min(remaining, currentBudget));
                          setAllUndefined(false);
                        }}
                      >
                        Max
                      </span>
                    }
                  />
                </Form.Item>
              </div>
            );
          })}

          {mode === "budget" && (
            <div className="p-3 border rounded-lg bg-amber-50">
              <Form.Item
                name="budget"
                label={`Wallet Topup (max $${maxBudgetTopup})`}
                rules={[
                  { required: true, message: "Please enter a topup amount." },
                  {
                    type: "number",
                    min: 1,
                    message: "Amount must be at least $1.",
                  },
                  {
                    validator: (_, val) => {
                      if (typeof val === "number" && val > maxBudgetTopup) {
                        return Promise.reject(
                          new Error(
                            `Topup cannot exceed the unfunded gap ($${maxBudgetTopup}). Wallet already holds $${currentBudget} of the $${totalRemaining} owed.`,
                          ),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                {/* <InputNumber disabled={disableBudgetMode} style={{ width: "100%" }} step={0.01} precision={2} /> */}
                <InputNumber
                  disabled={disableBudgetMode}
                  style={{ width: "100%" }}
                  step={0.01}
                  precision={2}
                  addonAfter={
                    <span
                      className={`text-xs font-semibold px-1 ${disableBudgetMode ? "text-gray-300 cursor-not-allowed" : "text-blue-500 hover:text-blue-700 cursor-pointer"}`}
                      onClick={() => {
                        if (disableBudgetMode) return;
                        form.setFieldValue("budget", maxBudgetTopup);
                        setAllUndefined(false);
                      }}
                    >
                      Max
                    </span>
                  }
                />
              </Form.Item>
            </div>
          )}

          {mode === "creditors" && dto.creditors.length === 0 && (
            <Alert
              type="warning"
              showIcon
              message="No creditor distribution found. Please set up creditor distribution before making payments."
              className="mb-2"
            />
          )}

          {mode === "creditors" &&
            dto.creditors.length > 0 &&
            (() => {
              const selectedIdx = dto.creditors.findIndex((c) => c.creditor_id === selectedCreditorId);
              const selected = selectedIdx !== -1 ? dto.creditors[selectedIdx] : null;
              const remaining = selected ? round2(selected.amount - (selected.paid ?? 0)) : 0;

              return (
                <>
                  <Form.Item label="Select Creditor">
                    <Select
                      placeholder="Pick a creditor to pay"
                      value={selectedCreditorId}
                      onChange={(val) => {
                        setSelectedCreditorId(val);
                        form.resetFields(["creditors"]);
                        setAllUndefined(true);
                      }}
                      disabled={disableModes}
                      options={dto.creditors.map((c) => ({
                        value: c.creditor_id,
                        label: `${c.creditor_name} — remaining $${round2(c.amount - (c.paid ?? 0))}`,
                        disabled: round2(c.amount - (c.paid ?? 0)) <= 0,
                      }))}
                    />
                  </Form.Item>

                  {selected && (
                    <div className="p-4 border rounded-xl bg-blue-50 space-y-4">
                      <Form.Item
                        name={["creditors", selectedIdx, "amount"]}
                        label={`Amount (remaining $${remaining})`}
                        rules={[
                          {
                            validator: (_, val) => {
                              if (val === undefined || val === null) return Promise.resolve();
                              if (remaining <= 0)
                                return Promise.reject(new Error("This creditor is already fully paid."));
                              if (val < 1) return Promise.reject(new Error("Amount must be at least $1."));
                              if (val > remaining)
                                return Promise.reject(
                                  new Error(`Amount cannot exceed the remaining balance ($${remaining}).`),
                                );
                              if (val > currentBudget)
                                return Promise.reject(
                                  new Error(`Amount cannot exceed the wallet balance ($${currentBudget}).`),
                                );
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        {/* <InputNumber disabled={disableModes} style={{ width: "100%" }} step={0.01} precision={2} /> */}
                        <InputNumber
                          disabled={disableModes}
                          style={{ width: "100%" }}
                          step={0.01}
                          precision={2}
                          addonAfter={
                            <span
                              className={`text-xs font-semibold px-1 ${disableModes ? "text-gray-300 cursor-not-allowed" : "text-blue-500 hover:text-blue-700 cursor-pointer"}`}
                              onClick={() => {
                                if (disableModes) return;
                                form.setFieldValue(
                                  ["creditors", selectedIdx, "amount"],
                                  Math.min(remaining, currentBudget),
                                );
                                setAllUndefined(false);
                              }}
                            >
                              Max
                            </span>
                          }
                        />
                      </Form.Item>
                      <div className="grid grid-cols-2 gap-4">
                        <Form.Item
                          name={["creditors", selectedIdx, "ref_transaction_id"]}
                          label="Transaction Reference ID"
                        >
                          <Input disabled={disableModes} />
                        </Form.Item>

                        <Form.Item name={["creditors", selectedIdx, "posting_date"]} label="Posting Date">
                          <DatePicker style={{ width: "100%" }} disabled={disableModes} />
                        </Form.Item>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
        </div>

        {mode !== "creditors" && (
          <div className="mt-2 p-4 border rounded-xl bg-gray-50 space-y-4">
            <div className="text-sm font-medium text-gray-700">Transaction Details (optional)</div>

            <div className="grid grid-cols-2 gap-4">
              <Form.Item
                name="ref_transaction_id"
                label={<span className="text-xs text-gray-500">Reference ID</span>}
                className="mb-0"
              >
                <Input placeholder="Enter reference transaction ID" disabled={isLockedOrPaid} className="rounded-lg" />
              </Form.Item>

              <Form.Item
                name="posting_date"
                label={<span className="text-xs text-gray-500">Posting Date</span>}
                className="mb-0"
              >
                <DatePicker style={{ width: "100%" }} disabled={isLockedOrPaid} className="rounded-lg" />
              </Form.Item>
            </div>
          </div>
        )}

        {dto.week === 0 && (
          <Alert
            showIcon
            type="info"
            message="The posting dates for payment of subsequent weeks will be created once all the components are fully paid for this week."
            className="mt-4"
          />
        )}
      </Form>
      <Modal
        open={fullSettleOpen}
        onCancel={() => setFullSettleOpen(false)}
        title={
          <Space align="center">
            <span>Confirm Full Row Settlement</span>
          </Space>
        }
        okButtonProps={{ style: { display: "none" } }}
        cancelText="Cancel"
        footer={(_, { CancelBtn }) => (
          <Space>
            <CancelBtn />
            <Button
              type="primary"
              icon={<TbAutomation size={14} />}
              loading={fullSettleLoading}
              disabled={fullSettleLoading}
              onClick={() => {
                const ref = form.getFieldValue("fully_paid_ref_transaction_id");
                const date = form.getFieldValue("fully_paid_posting_date");
                setFullSettleLoading(true);
                markFullyPaid.mutate(
                  {
                    body: {
                      idx: dto.category === LEDGER_CATEGORY.MONTHLY ? dto.week * 4 : dto.week,
                      application_id: applicationId,
                      ref_transaction_id: ref,
                      posting_date: date ? ((date as any).toDate?.() ?? new Date(date as any)) : undefined,
                      settlementType: "automation",
                    },
                  },
                  {
                    onSuccess: () => {
                      message.success("Ledger has been fully marked.");
                      form.resetFields();
                      setFullSettleOpen(false);
                      setFullSettleLoading(false);
                      queryClient.invalidateQueries({ queryKey: ["ledger/budget", dto._id] });
                      queryClient.invalidateQueries({ queryKey: ["application/payment-table", applicationId] });
                      queryClient.invalidateQueries({ queryKey: ["application"] });
                      queryClient.invalidateQueries({ queryKey: ["application", "wallet/balance", applicationId] });
                      clear();
                    },
                    onError: () => setFullSettleLoading(false),
                  },
                );
              }}
            >
              Automate next week
            </Button>
            <Button
              type="primary"
              icon={<TbCreditCard size={14} />}
              loading={fullSettleLoading}
              disabled={fullSettleLoading}
              onClick={() => {
                const ref = form.getFieldValue("fully_paid_ref_transaction_id");
                const date = form.getFieldValue("fully_paid_posting_date");
                setFullSettleLoading(true);
                markFullyPaid.mutate(
                  {
                    body: {
                      idx: dto.week,
                      application_id: applicationId,
                      ref_transaction_id: ref,
                      posting_date: date ? ((date as any).toDate?.() ?? new Date(date as any)) : undefined,
                      settlementType: "manual",
                    },
                  },
                  {
                    onSuccess: () => {
                      message.success("Ledger has been fully marked.");
                      form.resetFields();
                      setFullSettleOpen(false);
                      setFullSettleLoading(false);
                      queryClient.invalidateQueries({ queryKey: ["ledger/budget", dto._id] });
                      queryClient.invalidateQueries({ queryKey: ["application/payment-table", applicationId] });
                      queryClient.invalidateQueries({ queryKey: ["application", "wallet/balance", applicationId] });
                      clear();
                    },
                    onError: () => setFullSettleLoading(false),
                  },
                );
              }}
            >
              Mark week manually
            </Button>
          </Space>
        )}
      >
        <Space direction="vertical" size={18} style={{ width: "100%" }}>
          <Alert
            type="warning"
            message="If starting automation action is selected, it will manually settle the ledger row and start the automated weekly payments for the subsequent rows. Refer to the posting date that will be created after proceeding with this action to see the payment dates. Optionally, you may resolve the payment manually as before which will not trigger the automation."
          />
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <TbLock size={20} style={{ marginTop: 2 }} />
            <div>
              <Text strong>Ledger and application locking</Text>
              <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 12 }}>
                Once automation begins, most application details, ledger rows, and related payment configurations will
                no longer be editable or removable on/after the posting date. Please ensure all application details,
                payment methods, and ledger information are finalized before proceeding to ensure seamless automated
                payment processing.
              </Paragraph>
            </div>
          </div>
        </Space>
      </Modal>
      <Modal
        open={partialSettleOpen}
        onCancel={() => setPartialSettleOpen(false)}
        title={
          <Space align="center">
            <span>Confirm Full Row Settlement</span>
          </Space>
        }
        okButtonProps={{ style: { display: "none" } }}
        cancelText="Cancel"
        footer={(_, { CancelBtn }) => (
          <Space>
            <CancelBtn />
            <Button
              type="primary"
              icon={<TbAutomation size={14} />}
              loading={partialSettleLoading}
              disabled={partialSettleLoading}
              onClick={() => {
                settlementModeRef.current = "automation";
                setPartialSettleLoading(true);
                queryClient.invalidateQueries({ queryKey: ["application"] });
                form.submit();
              }}
            >
              Automate next week
            </Button>
            <Button
              type="primary"
              icon={<TbCreditCard size={14} />}
              loading={partialSettleLoading}
              disabled={partialSettleLoading}
              onClick={() => {
                settlementModeRef.current = "manual";
                setPartialSettleLoading(true);
                form.submit();
              }}
            >
              Mark week manually
            </Button>
          </Space>
        )}
      >
        <Space direction="vertical" size={18} style={{ width: "100%" }}>
          <Alert
            type="warning"
            message="If starting automation action is selected, it will manually settle the ledger row and start the automated weekly payments for the subsequent rows. Refer to the posting date that will be created after proceeding with this action to see the payment dates. Optionally, you may resolve the payment manually as before which will not trigger the automation."
          />
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <TbLock size={20} style={{ marginTop: 2 }} />
            <div>
              <Text strong>Ledger and application locking</Text>
              <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 12 }}>
                Once automation begins, most application details, ledger rows, and related payment configurations will
                no longer be editable or removable on/after the posting date. Please ensure all application details,
                payment methods, and ledger information are finalized before proceeding to ensure seamless automated
                payment processing.
              </Paragraph>
            </div>
          </div>
        </Space>
      </Modal>
    </Modal>
  );
}

const ExpandedLogs = ({ id }: { id: string }) => {
  const logsData = ApiService.ledger.useGetTransactionLogs(id);
  const logs = logsData.data?.result;

  return <pre>{JSON.stringify(logs, null, 2)}</pre>;
};

export function PaymentLedger({
  applicationId,
  loans,
  appKey,
  refetchData,
}: {
  applicationId: string | undefined;
  loans?: T_Populate<LoanSchemaDto, { attachments: FileSchemaDto[] }>[];
  appKey: string | undefined;
  refetchData: () => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const paymentTable = ApiService.application.useGetPaymentTable(applicationId);
  const editMetaInfo = ApiService.upstream.useEditMetaInfo();
  const [markedLedger, setMarkedLedger] = useState<LedgerSchemaDto>();
  const [isProgramFeeEditing, setIsProgramFeeEditing] = useState(false);
  const [programFeeValue, setProgramFeeValue] = useState<number | null>(null);
  const [selectedLedgerId, setSelectedLedgerId] = useState<string | null>(null);
  const [selectedLedgerIdForNotes, setSelectedLedgerIdForNotes] = useState<string | null>(null);
  const [selectedLedgerIdxForAutomation, setSelectedLedgerIdxForAutomation] = useState<number | undefined>(undefined);
  const [isPercentageLedgerModal, setIsPercentageLedgerModal] = useState(false);
  const createPercentageLedger = ApiService.ledger.useCreatePercentageLedger();
  // Track both ledger ID and creditor ID for creditor editing
  const [editingCreditor, setEditingCreditor] = useState<{ ledgerId: string; creditorId: string } | null>(null);
  const [creditorEditValue, setCreditorEditValue] = useState<number | null>(null);
  const paymentLedgerUpdate = ApiService.application.useUpdatePaymentLedger();
  const pauseLedgerRow = ApiService.ledger.usePauseLedgerRow();
  const modifyLedgerPostingDate = ApiService.ledger.useModifyPostingDate();
  const partialExecute = ApiService.application.usePartialExecute();
  const modifyNumberOfRows = ApiService.ledger.useModifyRows();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [notesContent, setNotesContent] = useState<string>("");
  const [modifyPostingModal, setModifyPostingModal] = useState<{
    open: boolean;
    record: LedgerSchemaDto | null;
  }>({ open: false, record: null });
  const [openPaymentAutomationStartModal, setOpenPaymentAutomationStartModal] = useState(false);
  const saveLedgerNotes = ApiService.application.useUpdateLedgerNotes();
  const fullApplication = ApiService.application.useGetByAppKey(appKey);
  const strategyType = fullApplication.data?.data.application.strategy_type;
  const application = fullApplication.data?.application;
  const [form] = Form.useForm();
  const pctProps = {
    step: 0.01,
    min: 0,
    max: 100,
    formatter: (value: any) => (value ? `${value}%` : "0%"),
    parser: (value: any) => (value ? Number.parseFloat(value.replace("%", "")) : 0),
    className: "!w-full",
  };
  const onSubmit = (values: { monthly_revenue: number; profit_percentage: number }) => {
    editMetaInfo.mutate(
      {
        summary: {
          ...application?.summary,
          monthly_revenue: values.monthly_revenue,
          profit_percentage: values.profit_percentage,
        },
        appkey: appKey as string,
      },
      {
        onSuccess: () => {
          createPercentageLedger.mutate(
            { appKey, lastWeek: paymentTable.data?.payment_table.at(-1)?.week ?? 0, weeks: 4 },
            {
              onSuccess: () => {
                message.success("ledger create for next 4 weeks");
                queryClient.invalidateQueries({ queryKey: ["application/payment-table"] });
                setIsPercentageLedgerModal(false);
              },
            },
          );
        },
      },
    );
  };

  useEffect(() => {
    if (application) {
      form.setFieldsValue({
        monthly_revenue: application.summary?.monthly_revenue,
        profit_percentage: application.summary?.profit_percentage ?? 0,
      });
    }
  }, [application]);

  const [isCreditorListHidden, setIsCreditorListHidden] = useState(true);
  const handleSaveLedgerNotes = () => {
    if (!selectedLedgerIdForNotes) return;
    saveLedgerNotes.mutate(
      {
        appkey: appKey as string,
        ledger_id: selectedLedgerIdForNotes,
        notes: notesContent,
      },
      {
        onSuccess: () => {
          message.success("Ledger notes updated successfully");
          setNotesContent("");
          setIsDrawerOpen(false);
          setSelectedLedgerIdForNotes(null);
          queryClient.invalidateQueries({ queryKey: ["application/payment-table"] });
        },
      },
    );
  };
  const updatePaymentLedger = (mode: "single" | "multiple", idx: number) => {
    paymentLedgerUpdate.mutate(
      {
        appkey: appKey as string,
        idx: idx,
        mode: mode,
        program_fee: programFeeValue ?? undefined,
        creditor: editingCreditor?.creditorId
          ? { id: editingCreditor?.creditorId, amount: creditorEditValue as number }
          : undefined,
      },
      {
        onSuccess: () => {
          if (programFeeValue) {
            message.success("Program Fee value updated succesfully");
          }
          if (creditorEditValue) {
            message.success("Creditor value updated succesfully");
          }
          setIsProgramFeeEditing(false);
          setSelectedLedgerId(null);
          setProgramFeeValue(null);
          setEditingCreditor(null);
          setCreditorEditValue(null);
          // queryClient.invalidateQueries({ queryKey: ["application", appKey] });
          paymentTable.refetch();
        },
      },
    );
  };
  const handleCreditorPartialExecute = (
    body: NonNullable<ReturnType<typeof ApiService.application.usePartialExecute>["variables"]>,
  ) => {
    partialExecute.mutate(body, {
      onSuccess: (data) => {
        if (data?.code === 201) {
          message.success("Creditor executed successfully");
          setIsProgramFeeEditing(false);
          setSelectedLedgerId(null);
          setProgramFeeValue(null);
          setEditingCreditor(null);
          setCreditorEditValue(null);
          // queryClient.invalidateQueries({ queryKey: ["application", appKey] });
          paymentTable.refetch();
          return;
        } else {
          if (data?.result?.error?.code === "PL001") {
            return Modal.confirm({
              title: "Creditor payment already exists for some weeks. Are you sure you want to override those?",
              okText: "Confirm",
              onOk: () => {
                handleCreditorPartialExecute({
                  ...body,
                  overwriteExists: true,
                });
              },
            });
          } else if (data?.result?.error?.code === "PL002") {
            return Modal.confirm({
              title: data?.result?.error?.message,
              okText: "Confirm",
              onOk: () => {
                modifyNumberOfRows.mutate(
                  { application_id: applicationId as string, count: data?.result?.error?.extras?.weeks ?? 0 },
                  {
                    onSuccess: () => {
                      handleCreditorPartialExecute(body);
                    },
                  },
                );
              },
            });
          }
        }
      },
    });
  };

  const isMonthly = paymentTable.data?.payment_table.some((entry) => entry.category === LEDGER_CATEGORY.MONTHLY);

  const paymentReadinessApplication = () => {
    const adminApp = fullApplication.data?.application;
    const gcApp = fullApplication.data?.data.application;

    return {
      unlinkedLoans: gcApp?.loans.some((l) => !l.creditor_id),
      unlinkedPartnerCreditors: gcApp?.loans.some((l) => !l.partner_creditor_id),
      pandaDocComplete: gcApp?.status.signatures === true,
      hasPaymentMethods: adminApp?.payment_info?.length !== 0,
    };
  };

  const paymentColumns: TableProps<LedgerSchemaDto>["columns"] = [
    {
      key: "week",
      dataIndex: "week",
      title: isMonthly ? "Month" : "Week",
      fixed: "left",
      width: 100,
      render: (val) => (val === 0 ? "Initial" : val),
    },
    {
      key: "payment",
      dataIndex: "amount",
      title: "Payment",
      align: "right",
      render: (val, record) => {
        const distribution_sum =
          record.distribution.legal_fee + record.distribution.service_fee + record.distribution.program_fee;

        const amount = Math.max(
          distribution_sum + record.distribution.creditor_total,
          distribution_sum + (record.distribution.final_creditor_total ?? 0),
        );

        return typeof amount === "number" ? formatMoney(amount) : "-";
      },
    },
    {
      key: "program_fee",
      dataIndex: "program_fee",
      title: "Program Fee",
      align: "right",
      render: (val, record, idx: number) => {
        const amount = record?.distribution?.program_fee;
        const paidAmount = record?.payment_statuses?.program_fee ?? 0;
        const diff = amount - paidAmount;
        const paid = paidAmount === amount;
        if (typeof amount !== "number") return "-";
        return (
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "8px" }}>
            {isProgramFeeEditing && record.week !== 0 && selectedLedgerId === record._id ? (
              <InputNumber
                controls={false}
                prefix="$"
                value={programFeeValue ?? amount}
                onChange={(val) => setProgramFeeValue(val)}
                style={{ width: "140px" }}
                formatter={(value) => {
                  const [start, end] = `${value}`.split(".") || [];
                  const v = `${start}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                  return `${end ? `${v}.${end}` : `${v}`}`;
                }}
                parser={(value) => value?.replace(/\$\s?|(,*)/g, "") as unknown as number}
              />
            ) : paidAmount > 0 && diff > 0 ? (
              <>
                <span style={{ textDecoration: "line-through", color: "#999" }}>{formatMoney(amount)}</span>
                <span style={{ fontWeight: 500 }}>{`${formatMoney(diff)}`}</span>
              </>
            ) : (
              <span>{formatMoney(amount)}</span>
            )}
            {paid ? (
              <TbCircleCheckFilled style={{ color: "green" }} />
            ) : !(isProgramFeeEditing && selectedLedgerId === record._id) ? (
              <TbEdit
                onClick={() => {
                  setIsProgramFeeEditing(true);
                  setSelectedLedgerId(record._id);
                  setProgramFeeValue(amount);
                  setEditingCreditor(null);
                  setCreditorEditValue(null);
                }}
                style={{ cursor: "pointer", flexShrink: 0, display: record.week === 0 ? "none" : "initial" }}
              />
            ) : programFeeValue !== amount ? (
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: [
                    {
                      key: "single",
                      label: "Update single row",
                      onClick: () => {
                        updatePaymentLedger("single", idx);
                      },
                    },
                    {
                      key: "multiple",
                      label: "Update all rows till non-zero",
                      onClick: () => {
                        updatePaymentLedger("multiple", idx);
                      },
                    },
                  ],
                }}
              >
                <TbDotsVertical onClick={() => {}} style={{ cursor: "pointer", flexShrink: 0 }} />
              </Dropdown>
            ) : null}
          </div>
        );
      },
    },
    {
      key: "service_fee",
      dataIndex: "service_fee",
      title: "Service Fee",
      align: "right",
      render: (val, record) => {
        const amount = record?.distribution?.service_fee;
        const paidAmount = record?.payment_statuses?.service_fee ?? 0;
        const diff = amount - paidAmount;
        const paid = paidAmount === amount;

        if (typeof amount !== "number") return "-";

        return (
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6 }}>
            {paidAmount > 0 && diff > 0 ? (
              <>
                <span style={{ textDecoration: "line-through", color: "#8c8c8c" }}>{formatMoney(amount)}</span>
                <span style={{ fontWeight: 600 }}>{formatMoney(diff)}</span>
              </>
            ) : (
              <span>{formatMoney(amount)}</span>
            )}

            {paid && <TbCircleCheckFilled style={{ color: "green" }} />}
          </div>
        );
      },
    },
    {
      key: "legal_fee",
      dataIndex: "legal_fee",
      title: "Legal Fee",
      align: "right",
      render: (val, record) => {
        const amount = record?.distribution?.legal_fee ?? 0;
        const paidAmount = record?.payment_statuses?.legal_fee ?? 0;
        const diff = amount - paidAmount;
        const paid = paidAmount === amount;

        if (typeof amount !== "number") return "-";

        return (
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6 }}>
            {paidAmount > 0 && diff > 0 ? (
              <>
                <span style={{ textDecoration: "line-through", color: "#8c8c8c" }}>{formatMoney(amount)}</span>
                <span style={{ fontWeight: 600 }}>{formatMoney(diff)}</span>
              </>
            ) : (
              <span>{formatMoney(amount)}</span>
            )}

            {paid && <TbCircleCheckFilled style={{ color: "green" }} />}
          </div>
        );
      },
    },
    {
      key: "creditor",
      dataIndex: "creditor",
      title: (
        <div className="inline-flex items-center gap-4">
          <span>Creditor Total (estimate)</span>
          <Tooltip title={`Click to ${isCreditorListHidden ? "show" : "hide"} all creditor fees`}>
            <Button
              type="text"
              icon={
                isCreditorListHidden ? (
                  <TbSquareChevronRightFilled className="!fill-gray-400" />
                ) : (
                  <TbSquareChevronLeftFilled className="!fill-gray-400" />
                )
              }
            />
          </Tooltip>
        </div>
      ),
      align: "right",
      onHeaderCell: () => {
        return {
          onClick: () => {
            setIsCreditorListHidden(!isCreditorListHidden);
          },
        };
      },
      render: (val, record) => {
        const paidAmount = record?.payment_statuses?.creditor_total ?? 0;
        const amount = record?.distribution?.creditor_total ?? 0;
        const diff = amount - paidAmount;
        const paid = paidAmount === amount;

        if (typeof amount !== "number") return "-";

        return (
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6 }}>
            {paidAmount > 0 && diff > 0 ? (
              <>
                <span style={{ textDecoration: "line-through", color: "#8c8c8c" }}>{formatMoney(amount)}</span>
                <span style={{ fontWeight: 600 }}>{formatMoney(diff)}</span>
              </>
            ) : (
              <span>{formatMoney(amount)}</span>
            )}

            {(amount === 0 || paid) && <TbCircleCheckFilled style={{ color: "green" }} />}
          </div>
        );
      },
    },
    {
      key: "creditors",
      dataIndex: "creditor",
      title: "Creditor Breakdown",
      align: "center",
      hidden: isCreditorListHidden,
      width: loans ? (isCreditorListHidden ? 0 : loans.length * 100) : 0,
      onHeaderCell: () => {
        return {
          onClick: () => {
            setIsCreditorListHidden(true);
          },
        };
      },
      children: loans?.map((el) => ({
        title: el?.creditor_name,
        key: el?._id,
        align: "right",
        render: (val, record, idx: number) => {
          const thisCreditor = record?.creditors?.find((creditor) => creditor?.creditor_id === el?._id);
          const isEditing = editingCreditor?.ledgerId === record._id && editingCreditor?.creditorId === el?._id;

          return (
            <div className="flex gap-2 items-center justify-end">
              {isEditing && record.week !== 0 ? (
                <InputNumber
                  value={creditorEditValue ?? thisCreditor?.amount}
                  onChange={(value) => setCreditorEditValue(value)}
                  controls={false}
                  prefix="$"
                  style={{ width: "140px" }}
                  formatter={(value) => {
                    const [start, end] = `${value}`.split(".") || [];
                    const v = `${start}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                    return `${end ? `${v}.${end}` : `${v}`}`;
                  }}
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "") as unknown as number}
                />
              ) : (
                <>
                  {thisCreditor
                    ? (() => {
                        const amount = thisCreditor.amount ?? 0;
                        const paid = thisCreditor.paid ?? 0;

                        if (paid > 0 && paid < amount) {
                          return (
                            <span className="flex items-center gap-2">
                              <span className="line-through text-gray-400">{formatMoney(amount)}</span>
                              <span className="font-semibold text-gray-800">{formatMoney(amount - paid)}</span>
                            </span>
                          );
                        }

                        if (paid >= amount && amount > 0) {
                          return <span className="font-semibold text-green-600">{formatMoney(amount)}</span>;
                        }

                        return <span>{formatMoney(amount)}</span>;
                      })()
                    : "-"}
                </>
              )}
              {!isEditing ? (
                thisCreditor &&
                record.week !== 0 &&
                !isEditing && (
                  <TbEdit
                    onClick={() => {
                      setIsProgramFeeEditing(false);
                      setProgramFeeValue(null);
                      setEditingCreditor({
                        ledgerId: record._id,
                        creditorId: el?._id,
                      });
                      setCreditorEditValue(thisCreditor.amount);
                    }}
                    style={{ cursor: "pointer", flexShrink: 0 }}
                  />
                )
              ) : creditorEditValue !== thisCreditor?.amount ? (
                <Dropdown
                  trigger={["click", "hover"]}
                  menu={{
                    items: [
                      {
                        key: "single",
                        label: "Update single row",
                        onClick: () => {
                          updatePaymentLedger("single", idx);
                        },
                      },
                      {
                        key: "multiple",
                        label: "Update all rows till non-zero",
                        onClick: () => {
                          handleCreditorPartialExecute({
                            appkey: appKey as string,
                            creditor_id: editingCreditor.creditorId,
                            start_week: idx,
                            weekly_payment: creditorEditValue as number,
                          });
                        },
                      },
                    ],
                  }}
                >
                  <TbDotsVertical onClick={() => {}} style={{ cursor: "pointer", flexShrink: 0 }} />
                </Dropdown>
              ) : null}
            </div>
          );
        },
        // typeof record?.creditors?.?.creditor_total === "number"
        //   ? formatMoney(record?.distribution?.creditor_total)
        //   : "-",
      })),
    },
    {
      key: "posting_date",
      dataIndex: "posting_date",
      title: "Posting Date",
      align: "right",
      render: (val, record) =>
        typeof val === "string" && record.posting_date !== undefined
          ? dayjs(record.posting_date).tz("America/New_York").format("MM/DD/YYYY, hh:mm A")
          : "-",
    },
    {
      key: "payment_date",
      dataIndex: "payment_date",
      title: "Payment Date",
      align: "right",
      render: (val, record) =>
        typeof val === "string" && record.payment_date !== undefined
          ? dayjs(record.payment_date).format("MM/DD/YYYY, hh:mm A")
          : "-",
    },
    {
      key: "status",
      dataIndex: "status",
      title: "Status",
      align: "right",
      render: (val, record) => (
        <div className="flex flex-col items-end gap-1">
          <Tag color={getLedgerStatusColors(val)}>{val.toUpperCase()}</Tag>
          {record.restructured_to && (
            <Tooltip
              title={
                record.category === LEDGER_CATEGORY.MONTHLY
                  ? `Rescheduled to month ${Math.floor(record.restructured_to / 4)}`
                  : `Rescheduled to week ${record.restructured_to}`
              }
            >
              <Tag color="orange" className="text-xs">
                Rescheduled→{" "}
                {record.category === LEDGER_CATEGORY.MONTHLY
                  ? `M${Math.floor(record.restructured_to / 4)}`
                  : `W${record.restructured_to}`}
              </Tag>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: "actions",
      title: "Actions",
      align: "right",
      fixed: "right",
      width: 85,
      render: (val, record) => (
        <div className="flex items-center gap-1">
          {/* <Button variant="outlined" color="primary" size="small" onClick={() => setMarkedLedger(record)}>
            Mark as Paid
          </Button> */}
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "paid",
                  label: "Mark as Paid",
                  icon: <TbCircleCheckFilled />,
                  disabled:
                    paymentTable?.data?.payment_table?.[0]?.status !== TRANSACTION_STATUS.PAID && record.week !== 0,
                  onClick: () => {
                    console.log(record);
                    setMarkedLedger(record);
                  },
                },
                {
                  key: "payment-jobs",
                  label: "Monitor Payment Logs",
                  icon: <TbEyeDollar />,
                  onClick: () =>
                    navigate(
                      `/ledger/payment-monitor?ledger_id=${record._id}&application_id=${record.application_id}&appkey=${appKey}`,
                    ),
                },
                {
                  key: "modify-posting-date",
                  label: "Modify Posting Day",
                  icon: <FiCalendar />,
                  onClick: () => setModifyPostingModal({ open: true, record }),
                },
                {
                  key: "pause-row",
                  label: "Reschedule Payment",
                  disabled: record.week === 0,
                  icon: <FiStopCircle />,
                  onClick: () =>
                    Modal.confirm({
                      width: 600,
                      title: "Reschedule Payment",
                      content: (
                        <div className="mt-4">
                          <Paragraph>
                            The payment for this week/month will be rescheduled after the currently present last
                            week/month of this ledger.
                          </Paragraph>

                          <Alert
                            type="warning"
                            showIcon
                            message="Legal and service fees are not affected"
                            description="This action only reschedules the program fee and creditor payments for this row."
                          />

                          <Paragraph className="mt-3 mb-0">
                            <Text strong>Restriction:</Text> If this row has already been partially marked as paid
                            manually or if there is also an upcoming payment automation within 3 hours of its posting
                            date, it cannot be rescheduled.
                          </Paragraph>

                          <Paragraph className="mt-3 mb-0">Do you want to continue?</Paragraph>
                        </div>
                      ),
                      okText: "Pause",
                      okButtonProps: { danger: true, loading: pauseLedgerRow.isPending },
                      onOk: () =>
                        pauseLedgerRow.mutate(
                          { ledger_id: record._id },
                          {
                            onSuccess: (data) => {
                              message.success(data.message);
                              paymentTable.refetch();
                            },
                          },
                        ),
                    }),
                },
                {
                  key: "notes",
                  icon: <TbNotebook />,
                  label: record.note ? "Update Notes" : "Add Notes",
                  onClick: () => {
                    setSelectedLedgerIdForNotes(record._id);
                    setIsDrawerOpen(true);
                    setNotesContent(record.note ?? "");
                  },
                },
                {
                  key: "payment-automation",
                  icon: <TbAutomation />,
                  label: "Start Payment Automation",
                  onClick: () => {
                    setSelectedLedgerIdxForAutomation(record.week);
                    setOpenPaymentAutomationStartModal(true);
                  },
                },
              ],
            }}
          >
            <Button size="small" icon={<TbDotsVertical />}></Button>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      {markedLedger && applicationId ? (
        <MarkPaymentModal dto={markedLedger} clear={() => setMarkedLedger(undefined)} applicationId={applicationId} />
      ) : null}
      <StartPaymentAutomationModal
        appkey={appKey!}
        applicationId={fullApplication.data?.application?._id ?? ""}
        readiness={paymentReadinessApplication()}
        open={openPaymentAutomationStartModal}
        onClose={() => {
          setOpenPaymentAutomationStartModal(false);
          setSelectedLedgerIdxForAutomation(undefined);
        }}
        ledgerIdx={selectedLedgerIdxForAutomation}
      />
      <Table
        // expandable={{
        //   expandedRowRender: (record) => <ExpandedLogs id={record._id} />,
        //   columnWidth: 80,
        // }}
        className="editable-ledger-table"
        virtual
        scroll={{ y: 400, x: loans?.length && !isCreditorListHidden ? loans?.length * 100 + 1500 : 1500 }}
        rowKey={"_id"}
        bordered
        loading={{
          spinning: paymentTable.isFetching || paymentLedgerUpdate.isPending || partialExecute.isPending,
          indicator: loaderSm,
        }}
        columns={paymentColumns}
        dataSource={paymentTable.data?.payment_table
          .filter((entry) => entry.category !== LEDGER_CATEGORY.SKIP)
          .map((entry) => ({
            ...entry,
            week: isMonthly ? Math.floor(entry.week / 4) : entry.week,
          }))}
        summary={() => {
          const rows = paymentTable.data?.payment_table ?? [];
          const creditorColumnsCount = loans?.length ?? 0;
          const lastRow = rows.at(-1);

          return (
            <Table.Summary fixed="bottom">
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} className="bg-gray-100 font-bold text-gray-800">
                  Total
                </Table.Summary.Cell>

                <Table.Summary.Cell index={1} align="right" className="bg-gray-100 font-bold text-gray-800">
                  {formatMoney(rows.reduce((p, c) => p + (c.amount ?? 0), 0))}
                </Table.Summary.Cell>

                <Table.Summary.Cell index={2} align="right" className="bg-gray-100 font-bold text-gray-800">
                  {formatMoney(rows.reduce((p, c) => p + (c.distribution?.program_fee ?? 0), 0))}
                </Table.Summary.Cell>

                <Table.Summary.Cell index={3} align="right" className="bg-gray-100 font-bold text-gray-800">
                  {formatMoney(rows.reduce((p, c) => p + (c.distribution?.service_fee ?? 0), 0))}
                </Table.Summary.Cell>

                <Table.Summary.Cell index={4} align="right" className="bg-gray-100 font-bold text-gray-800">
                  {formatMoney(rows.reduce((p, c) => p + (c.distribution?.legal_fee ?? 0), 0))}
                </Table.Summary.Cell>

                <Table.Summary.Cell index={5} align="right" className="bg-gray-100 font-bold text-gray-800">
                  {formatMoney(rows.reduce((p, c) => p + (c.distribution?.creditor_total ?? 0), 0))}
                </Table.Summary.Cell>

                {loans?.map((loan, idx) => {
                  const total = rows.reduce((sum, row) => {
                    const creditor = row.creditors?.find((c) => c.creditor_id === loan._id);
                    return sum + (creditor?.amount ?? 0);
                  }, 0);

                  return (
                    <Table.Summary.Cell
                      key={loan._id}
                      index={6 + idx}
                      align="right"
                      className="bg-gray-100 font-bold text-gray-800"
                    >
                      {total ? formatMoney(total) : "-"}
                    </Table.Summary.Cell>
                  );
                })}

                <Table.Summary.Cell
                  index={6 + creditorColumnsCount}
                  colSpan={2}
                  className="bg-gray-100 font-bold text-gray-800"
                ></Table.Summary.Cell>
              </Table.Summary.Row>
              {strategyType && strategyType === StrategyType.PERCENTAGE && (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} className="bg-gray-100 !text-xs font-semibold text-gray-800">
                    Remaining
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={1} align="right" className="bg-gray-100 font-bold text-gray-800">
                    {formatMoney(lastRow?.remaining_amount?.total ?? 0)}
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={2} align="right" className="bg-gray-100 font-bold text-gray-800">
                    {formatMoney(lastRow?.remaining_amount?.program_fee ?? 0)}
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={3} align="right" className="bg-gray-100 font-bold text-gray-800">
                    {/* {formatMoney(rows.reduce((p, c) => p + (c.distribution?.service_fee ?? 0), 0))} */}
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={4} align="right" className="bg-gray-100 font-bold text-gray-800">
                    {/* {formatMoney(rows.reduce((p, c) => p + (c.distribution?.legal_fee ?? 0), 0))} */}
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={5} align="right" className="bg-gray-100 font-bold text-gray-800">
                    {formatMoney(lastRow?.remaining_amount?.creditor_fee ?? 0)}
                  </Table.Summary.Cell>

                  {loans?.map((loan, idx) => {
                    const total = rows.reduce((sum, row) => {
                      const creditor = row.creditors?.find((c) => c.creditor_id === loan._id);
                      return sum + (creditor?.amount ?? 0);
                    }, 0);

                    return (
                      <Table.Summary.Cell
                        key={loan._id}
                        index={6 + idx}
                        align="right"
                        className="bg-gray-100 font-bold text-gray-800"
                      >
                        {total ? formatMoney(total) : "-"}
                      </Table.Summary.Cell>
                    );
                  })}

                  <Table.Summary.Cell
                    index={6 + creditorColumnsCount}
                    colSpan={2}
                    className="bg-gray-100 font-bold text-gray-800"
                  ></Table.Summary.Cell>
                </Table.Summary.Row>
              )}
              {/* <Table.Summary.Row>
                <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>

               
                <Table.Summary.Cell index={6 + creditorColumnsCount} align="right" colSpan={2}>
                  <Button type="primary" onClick={() => console.log("Clicked")}>
                    Submit
                  </Button>
                </Table.Summary.Cell>
              </Table.Summary.Row> */}
            </Table.Summary>
          );
        }}
        // footer={() => {
        //   const rows = paymentTable.data?.payment_table ?? [];
        //   const creditorColumnsCount = loans?.length ?? 0;
        //   const lastRow = rows.at(-1);
        //   const lastRowRemainingTotal = lastRow?.remaining_amount?.total ?? 0;
        //   return (
        //     <div className="flex justify-end">
        //       <Button
        //         type="primary"
        //         loading={editMetaInfo.isPending || createPercentageLedger.isPending}
        //         onClick={() => setIsPercentageLedgerModal(true)}
        //       >
        //         Execute All
        //       </Button>
        //     </div>
        //   );
        // }}
        footer={
          //   const rows = paymentTable.data?.payment_table ?? [];
          //   const creditorColumnsCount = loans?.length ?? 0;
          //   const lastRow = rows.at(-1);
          //   const lastRowRemainingTotal = lastRow?.remaining_amount?.total ?? 0;
          strategyType === StrategyType.PERCENTAGE
            ? () => {
                const rows = paymentTable.data?.payment_table ?? [];
                const creditorColumnsCount = loans?.length ?? 0;
                const lastRow = rows.at(-1);
                const lastRowRemainingTotal = lastRow?.remaining_amount?.total ?? 0;
                return (
                  <div className="flex justify-end">
                    <Button
                      type="primary"
                      disabled={lastRowRemainingTotal === 0}
                      loading={editMetaInfo.isPending || createPercentageLedger.isPending}
                      onClick={() => setIsPercentageLedgerModal(true)}
                    >
                      Generate For Next 4 weeks
                    </Button>
                  </div>
                );
              }
            : undefined
        }
        pagination={false}
        sticky={{ offsetHeader: 64, offsetScroll: 0 }}
      />
      <Drawer
        title="Add Notes"
        width={500}
        onClose={() => {
          setIsDrawerOpen(false);
        }}
        open={isDrawerOpen}
        destroyOnHidden
      >
        <div className="p-6">
          <RichTextEditor value={notesContent} onChange={setNotesContent} />
          <div className="flex justify-end mt-4">
            <Button
              disabled={!notesContent}
              onClick={handleSaveLedgerNotes}
              loading={saveLedgerNotes.isPending}
              type="primary"
            >
              Save
            </Button>
          </div>
        </div>
      </Drawer>
      <ModifyPostingDayModal
        open={modifyPostingModal.open}
        record={modifyPostingModal.record}
        onClose={() => setModifyPostingModal({ open: false, record: null })}
        onSuccess={() => paymentTable.refetch()}
      />
      <Modal
        centered
        open={isPercentageLedgerModal}
        onCancel={() => setIsPercentageLedgerModal(false)}
        onOk={() => {
          form.submit();
        }}
        okText="Submit"
        okButtonProps={{ loading: editMetaInfo.isPending || createPercentageLedger.isPending }}
      >
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item label="Monthly Revenue" name={"monthly_revenue"}>
            <InputNumber
              formatter={(value) => {
                const [start, end] = `${value}`.split(".") || [];
                const v = `${start}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                return `${end ? `${v}.${end}` : `${v}`}`;
              }}
              parser={(value) => value?.replace(/\$\s?|(,*)/g, "") as unknown as number}
              prefix="$"
              className="!w-full"
            />
          </Form.Item>
          <Form.Item label="Profit Percentage" name={"profit_percentage"}>
            <InputNumber {...pctProps} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export function PandaDocModal({
  appKey,
  pandaDocs,
  isOpen,
  setIsOpen,
}: {
  appKey: string;
  pandaDocs: GetClearApplicationDto["external_links"]["pandadoc"];
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
}) {
  const [showDocId, setShowDocId] = useState<Record<string, boolean>>({});
  const [editMap, setEditMap] = useState<Record<string, string>>({});
  const changePandaDocs = ApiService.application.useChangePandaDocs(appKey);

  return (
    <Modal
      open={isOpen}
      onCancel={() => setIsOpen(false)}
      title="Change PandaDoc"
      okText="Change"
      okButtonProps={{
        loading: changePandaDocs.isPending,
        onClick: () => {
          const valid: Record<string, string> = {};
          Object.entries(editMap).forEach(([key, val]) => {
            if (val && val?.length > 0 && key !== val) {
              valid[key] = val;
            }
          });

          changePandaDocs.mutate(valid, {
            onSuccess: (data) => {
              const isAllSuccess = Object.values(data?.result?.upstream_response).every(
                (el) => el?.status === "success",
              );
              if (isAllSuccess) {
                setIsOpen(false);
              } else {
                console.log(data);
              }
            },
          });
        },
      }}
    >
      {pandaDocs?.map((el, i) => {
        console.log(el);

        return (
          <div key={el?.doc_id} className="flex gap-1 justify-between">
            <div className="grid grid-cols-[16px_auto] gap-1">
              <Typography.Text>{i + 1}.</Typography.Text>
              <div className="inline-flex gap-1 items-baseline">
                <Typography.Text>{el?.name}</Typography.Text>
                {el?.status ? <Tag>{el?.status}</Tag> : <div />}
              </div>
            </div>
            <div className="inline-flex gap-1">
              {showDocId?.[el?.doc_id] ? (
                <Input
                  defaultValue={el?.doc_id}
                  onChange={(e) => {
                    setEditMap({ ...editMap, [el?.doc_id]: e.target.value });
                  }}
                  style={{ fontVariantNumeric: "tabular-nums", width: 250, height: "fit-content" }}
                />
              ) : null}

              <Button
                type={showDocId?.[el?.doc_id] === true ? "primary" : "default"}
                icon={<TbEdit />}
                onClick={() => setShowDocId({ ...showDocId, [el?.doc_id]: !showDocId[el?.doc_id] })}
              />
            </div>
          </div>
        );
      })}
    </Modal>
  );
}

export function ResetModal({
  appKey,
  isOpen,
  onClose,
  status,
}: {
  appKey: string;
  isOpen: boolean;
  onClose: () => void;
  status: GetClearApplicationDto["status"];
}) {
  const [stepsToReset, setStepsToReset] = useState<
    Partial<Record<(typeof APPLICATION_STATUS)[keyof typeof APPLICATION_STATUS], boolean>>
  >({});
  const resetSteps = ApiService.application.useResetSteps(appKey);

  return (
    <Modal
      open={isOpen}
      title="Reset Steps"
      okText="Reset"
      okButtonProps={{
        loading: resetSteps.isPending,
        onClick: () => {
          const toReset: (typeof APPLICATION_STATUS)[keyof typeof APPLICATION_STATUS][] = [];
          Object.entries(stepsToReset).forEach(([key, val]) => {
            if (val === true) {
              toReset.push(key as any);
            }
          });

          resetSteps.mutate(
            { modules: toReset },
            {
              onSuccess: () => {
                message.success(
                  `${toReset?.map((el) => toTitleCase(el, { sep: "_" })).join(", ")} - steps are reset successfully`,
                );
                onClose();
                return queryClient.invalidateQueries({ queryKey: ["application", appKey, undefined] });
              },
            },
          );
        },
      }}
      onCancel={onClose}
    >
      <div className="flex flex-col gap-1">
        {Object.entries(APPLICATION_STATUS).map(([key, val]) => {
          if (val in status && status[val] === true) {
            return (
              <div className="flex gap-2" key={val}>
                <Checkbox
                  checked={!!(stepsToReset as any)[val as any]}
                  onChange={(e) => {
                    console.log(e.target.checked);

                    setStepsToReset({ ...stepsToReset, [val]: !(stepsToReset as any)[val as any] });
                  }}
                >
                  {toTitleCase(key, { sep: "_" })}
                </Checkbox>
              </div>
            );
          }
          return null;
        })}
      </div>
    </Modal>
  );
}

export function ChangeStatusModal({
  appKey,
  isOpen,
  onClose,
  // status,
}: {
  appKey: string;
  isOpen: boolean;
  onClose: () => void;
  status: GetClearApplicationDto["status"];
}) {
  const changeStatus = ApiService.application.useChangeStatus(appKey);

  return (
    <Modal
      open={isOpen}
      title="Mark PandaDoc as Signed?"
      okText="Yes"
      okButtonProps={{
        loading: changeStatus.isPending,
        onClick: () => {
          changeStatus.mutate(
            { status: APPLICATION_STATUS.SIGNATURES },
            {
              onSuccess: () => {
                message.success(`Status updated successfully`);
                queryClient.invalidateQueries({ queryKey: ["application", appKey, undefined] }).then(() => {
                  onClose();
                });
              },
            },
          );
        },
      }}
      onCancel={onClose}
    ></Modal>
  );
}

interface CreditorModalProps {
  selectedLoan?: T_Populate<
    LoanSchemaDto,
    {
      attachments: FileSchemaDto[];
    }
  >;
  open: boolean;
  onClose: () => void;
  appkey: string;
  source?: "create-application" | "application-page";
  isPlanSelected?: boolean;
  openUploadModal?: () => unknown;
  isPriorApplication?: boolean;
}

export const CreditorModal = ({
  onClose,
  open,
  selectedLoan,
  appkey,
  source = "application-page",
  isPlanSelected,
  openUploadModal,
  isPriorApplication = false,
}: CreditorModalProps) => {
  const overriddenFields = useRef<Set<string>>(new Set());
  const editLoan = ApiService.upstream.useEditLoan();
  const addLoan = ApiService.upstream.useAddLoan();

  const [prevFrequency, setPrevFrequency] = useState<FREQUENCY_TYPES | undefined>(undefined);
  const [prevDueAmount, setPrevDueAmount] = useState<number | undefined>(undefined);
  const [prevPaymnets, setPrevPaymnets] = useState<number | undefined>(undefined);
  // const [prev]

  const [defaultActiveKey, setDefaultActiveKey] = useState<string | undefined>("1");

  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const updateScrollHint = useCallback(() => {
    if (!scrollEl) return;
    setCanScrollDown(scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight > 8);
  }, [scrollEl]);

  useEffect(() => {
    if (!scrollEl) return;
    updateScrollHint();
    const observer = new ResizeObserver(updateScrollHint);
    observer.observe(scrollEl);
    if (scrollEl.firstElementChild) observer.observe(scrollEl.firstElementChild);
    return () => observer.disconnect();
  }, [scrollEl, updateScrollHint]);

  const scrollDown = () => {
    scrollEl?.scrollBy({ top: scrollEl.clientHeight * 0.8, behavior: "smooth" });
  };

  const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

  const formatter = (value: number | undefined) => {
    if (value === undefined) return "";
    const [start, end] = `${value}`.split(".") || [];
    const v = `${start}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return `${end ? `${v}.${end}` : `${v}`}`;
  };

  const roundedFormatter = (value: number | undefined, info: { userTyping: boolean }) => {
    if (value === undefined || `${value}` === "" || info.userTyping) return formatter(value);
    const num = Number(value);
    return formatter(Number.isFinite(num) ? round2(num) : value);
  };

  const parser = (value: string | undefined) => value?.replace(/\$\s?|(,*)/g, "") as unknown as number;
  const [creditorForm] = Form.useForm();
  const autocalculate = Form.useWatch("autocalculate", creditorForm);
  // console.log({ inside: selectedLoan });
  const handleLoanFrequencyChange = ({
    newFrequency,
    prevFrequency,
    record,
  }: {
    prevFrequency: FREQUENCY_TYPES | undefined;
    newFrequency: FREQUENCY_TYPES | undefined;
    record: T_Populate<LoanSchemaDto, { attachments: FileSchemaDto[] }>;
  }) => {
    const payments = record?.payments ?? 0;
    const frequencyChanged = !!prevFrequency && !!newFrequency && prevFrequency !== newFrequency;
    const newPaybackAmount = round2(
      frequencyChanged
        ? (payments / LoanFrequencyMultiplier[prevFrequency]) * LoanFrequencyMultiplier[newFrequency]
        : payments,
    );

    const updatedLoan = {
      ...record,
      frequency: newFrequency,
      payments: newPaybackAmount,
      manual_overrides: Array.from(overriddenFields.current),
    };
    const isFieldChanged =
      prevDueAmount !== record.due_amount || prevFrequency !== record.frequency || prevPaymnets !== record.payments;

    editLoan.mutate(
      { appkey, loan: updatedLoan as any, source: "application-page", isFieldsChanged: isFieldChanged },
      {
        onSuccess: (res) => {
          const editedLoan = res.result.editedLoan;
          message.success("Loan edited succesfully!");
          creditorForm.setFieldsValue({
            ...editedLoan,
            additional_fees:
              round2((editedLoan.origination_fees ?? 0) + (editedLoan.additional_fees_deductions ?? 0)) || undefined,
          });
          setPrevFrequency(editedLoan.frequency);
          setPrevDueAmount(editedLoan.due_amount);
          setPrevPaymnets(editedLoan.payments);
          queryClient.invalidateQueries({ queryKey: ["application", appkey] });
          overriddenFields.current = new Set();
        },
      },
    );
  };

  useEffect(() => {
    if (selectedLoan) {
      creditorForm.resetFields();
      setDefaultActiveKey("1");
      creditorForm.setFieldsValue({
        ...selectedLoan,
        additional_fees:
          round2((selectedLoan.origination_fees ?? 0) + (selectedLoan.additional_fees_deductions ?? 0)) || undefined,
      });
      setPrevFrequency(selectedLoan.frequency);
      setPrevDueAmount(selectedLoan.due_amount);
      setPrevPaymnets(selectedLoan.payments);
      overriddenFields.current = new Set();
    } else {
      creditorForm.resetFields();
      setDefaultActiveKey("1");
      overriddenFields.current = new Set();
    }
  }, [selectedLoan, creditorForm]);

  const markOverridden = (field: string) => () => {
    overriddenFields.current.add(field);
  };

  const footer = useMemo(() => {
    const items: React.ReactNode[] = [
      <Button
        key="cancel"
        onClick={() => {
          onClose();
          setDefaultActiveKey("1");
        }}
      >
        Cancel
      </Button>,
      <Button
        key="confirm"
        onClick={() => creditorForm.submit()}
        type="primary"
        variant="solid"
        loading={editLoan.isPending || addLoan.isPending}
      >
        {selectedLoan ? "Update Details" : "Add Details"}
      </Button>,
    ];

    if (openUploadModal) {
      items.push(
        <Button
          key="upload"
          onClick={() => {
            onClose();
            setDefaultActiveKey("1");
            openUploadModal();
          }}
          type="default"
          variant="solid"
          color="orange"
        >
          Upload Loan
        </Button>,
      );
    }

    return (
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Switch
            checked={autocalculate !== false}
            onChange={(checked) => creditorForm.setFieldValue("autocalculate", checked)}
          />
          <span className="text-sm">Autocalculate</span>
          <Tooltip title="When on, Net Purchase Price, Net Funded Amount, Additional Fees, Breakeven Amount and Balance to Total Debt % are recalculated on save. When off, values are saved exactly as entered.">
            <TbInfoCircle size={16} className="text-gray-400 cursor-help" />
          </Tooltip>
        </div>
        <div className="flex items-center gap-2">{items}</div>
      </div>
    );
  }, [
    onClose,
    setDefaultActiveKey,
    creditorForm,
    editLoan.isPending,
    addLoan.isPending,
    selectedLoan,
    openUploadModal,
    autocalculate,
  ]);

  return (
    <Modal
      destroyOnHidden
      title={selectedLoan?.creditor_name ?? "Add Creditor"}
      open={open}
      okText={selectedLoan ? "Update Details" : "Add Details"}
      onCancel={() => {
        onClose();
        setDefaultActiveKey("1");
      }}
      footer={footer}
      onOk={() => creditorForm.submit()}
      width={800}
      centered
      styles={{
        content: {
          padding: 24,
        },
      }}
    >
      <div className="relative">
        <div ref={setScrollEl} onScroll={updateScrollHint} className="overflow-y-auto pr-2" style={{ height: 600 }}>
          <div className="pb-4">
            <Form
              form={creditorForm}
              layout="vertical"
              onFinish={(formValues) => {
                const { additional_fees, ...values } = formValues;
                if (overriddenFields.current.has("additional_fees")) {
                  overriddenFields.current.add("origination_fees");
                  overriddenFields.current.add("additional_fees_deductions");
                  values.origination_fees = additional_fees ?? null;
                  values.additional_fees_deductions = 0;
                }

                if (selectedLoan) {
                  handleLoanFrequencyChange({
                    prevFrequency,
                    newFrequency: values.frequency,
                    record: values,
                  });
                } else {
                  addLoan.mutate(
                    {
                      appkey,
                      loan: {
                        ...values,
                        payments: typeof values.payments === "number" ? round2(values.payments) : values.payments,
                        manual_overrides: Array.from(overriddenFields.current),
                      },
                    },
                    {
                      onSuccess: async () => {
                        message.success("Loan added succesfully!");
                        creditorForm.resetFields();
                        onClose();
                        setDefaultActiveKey("1");
                        await queryClient.invalidateQueries({
                          queryKey: ["application", appkey],
                          refetchType: "active",
                        });
                        overriddenFields.current = new Set();
                      },
                    },
                  );
                }
              }}
            >
              <Form.Item hidden name="autocalculate" valuePropName="checked" initialValue={true}>
                <Switch />
              </Form.Item>
              <Form.Item hidden name="origination_fees" />
              <Form.Item hidden name="additional_fees_deductions" />
              <Collapse
                accordion
                expandIconPosition="end"
                items={[
                  {
                    key: "1",
                    label: "Loan Details",
                    onClick: () => {
                      setDefaultActiveKey(defaultActiveKey === "1" ? undefined : "1");
                    },
                    children: (
                      <>
                        <Form.Item hidden name={"_id"}></Form.Item>
                        <Form.Item label="Creditor Name" name={"creditor_name"} rules={[{ required: true }]}>
                          <Input placeholder="Creditor name" />
                        </Form.Item>
                        <div className="grid grid-cols-3 gap-4">
                          <Form.Item
                            label="Funded Amount"
                            name={"funded_amount"}
                            rules={[{ required: true }]}
                            tooltip="Raw funded amount from the document. Never autocalculated. Used to calculate Net Purchase Price."
                          >
                            <InputNumber
                              formatter={formatter}
                              parser={parser}
                              prefix="$"
                              style={{ width: "100%" }}
                              placeholder="Funded amount"
                              onChange={markOverridden("funded_amount")}
                            />
                          </Form.Item>
                          <Form.Item
                            label="Funded Date"
                            getValueProps={(value) => ({
                              value: value ? dayjs(value) : null,
                            })}
                            normalize={(value) => (value ? value.toDate() : null)}
                            name={"funded_date"}
                          >
                            {/* <InputNumber
                        formatter={formatter}
                        parser={parser}
                        prefix="$"
                        style={{ width: "100%" }}
                        placeholder="Funded amount"
                      /> */}
                            <DatePicker style={{ width: "100%" }} />
                          </Form.Item>
                          <Form.Item label="Payback Amount" name={"payback_amount"} rules={[{ required: true }]}>
                            <InputNumber
                              formatter={formatter}
                              parser={parser}
                              prefix="$"
                              style={{ width: "100%" }}
                              placeholder="Payback amount"
                            />
                          </Form.Item>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <Form.Item label="Balance Amount" name={"due_amount"} rules={[{ required: true }]}>
                            <InputNumber
                              formatter={formatter}
                              parser={parser}
                              prefix="$"
                              style={{ width: "100%" }}
                              placeholder="Balance amount"
                            />
                          </Form.Item>
                          <Form.Item label="Installation Payment" name={"payments"} rules={[{ required: true }]}>
                            <InputNumber
                              formatter={roundedFormatter}
                              parser={parser}
                              precision={2}
                              prefix="$"
                              style={{ width: "100%" }}
                              placeholder="Installation payment"
                            />
                          </Form.Item>
                          <Form.Item
                            label="Percentage Receivables"
                            name={"receivables"}
                            tooltip="Percentage of receivables"
                          >
                            <InputNumber<number>
                              min={0}
                              max={100}
                              formatter={(value) => (value ? `${value}%` : "")}
                              parser={(value) => parseFloat(value?.replace("%", "") || "0")}
                              style={{ width: "100%" }}
                            />
                          </Form.Item>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <Form.Item
                            label="Net Purchase Price"
                            name="net_purchase_price"
                            tooltip={
                              <>
                                {selectedLoan?.potential_refinance && (
                                  <div>This purchase may potentially be a refinance. Review the loan document.</div>
                                )}
                                <div>
                                  You may provide this manually, or leave it empty and save. The system will
                                  autocalculate it as Funded Amount − Additional Fees. It also recalculates when either
                                  of those two are edited.
                                </div>
                              </>
                            }
                          >
                            <InputNumber
                              formatter={formatter}
                              parser={parser}
                              prefix="$"
                              style={{ width: "100%" }}
                              placeholder="Net Purchase Price"
                              onChange={markOverridden("net_purchase_price")}
                            />
                          </Form.Item>
                          <Form.Item
                            label="Additional Fees"
                            name="additional_fees"
                            tooltip="Origination fees plus any other deductions (e.g. wire fees). If left empty and Net Purchase Price is provided, this will be calculated as Funded Amount − Net Purchase Price. Edit this field directly to override."
                          >
                            <InputNumber
                              formatter={formatter}
                              parser={parser}
                              prefix="$"
                              style={{ width: "100%" }}
                              placeholder="Additional fees"
                              onChange={markOverridden("additional_fees")}
                            />
                          </Form.Item>
                          {/* <Form.Item label="Loan Type" name={"loan_type"} rules={[{ required: true }]}>
                      <Select
                        options={Object.values(LOAN_TYPES).map((val) => {
                          return { label: val.toUpperCase(), value: val };
                        })}
                        placeholder="Loan Type"
                      />
                    </Form.Item> */}
                          {/* <Form.Item label="Tenure" name={"tenure"} rules={[{ required: true }]}>
                      <InputNumber style={{ width: "100%" }} placeholder="Tenure" />
                    </Form.Item> */}
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          {/* Hidden for now — backend still derives net_funded_amount from Net Purchase Price.
                          <Form.Item
                            label="Net Funded Amount"
                            name="net_funded_amount"
                            tooltip="Mirrors Net Purchase Price. Used to calculate Breakeven Amount."
                          >
                            <InputNumber
                              formatter={formatter}
                              parser={parser}
                              prefix="$"
                              style={{ width: "100%" }}
                              placeholder="Net funded amount"
                              onChange={markOverridden("net_funded_amount")}
                            />
                          </Form.Item>
                          */}
                          <Form.Item
                            label="Breakeven Amount"
                            name="breakeven_amount"
                            tooltip="The system will autocalculate it once Balance Amount is available, using Net Funded Amount × 1.1 as the breakeven base: Net Funded Amount × 1.1 − (Payback Amount − Balance Amount). Recalculated on save when Autocalculate is on. Edit this field directly to override."
                          >
                            <InputNumber
                              formatter={formatter}
                              parser={parser}
                              prefix="$"
                              style={{ width: "100%" }}
                              placeholder="Breakeven amount"
                              onChange={markOverridden("breakeven_amount")}
                            />
                          </Form.Item>
                          <Form.Item
                            label="Balance to Total Debt of this loan"
                            name="balance_to_totaldebt_percentage"
                            tooltip="The system will autocalculate it once Balance Amount is available: (Balance Amount ÷ Payback Amount) × 100. Recalculated on save when Autocalculate is on. Edit this field directly to override."
                          >
                            <InputNumber<number>
                              min={0}
                              max={100}
                              formatter={(value) => (value ? `${value}%` : "")}
                              parser={(value) => parseFloat(value?.replace("%", "") || "0")}
                              style={{ width: "100%" }}
                              placeholder="Balance / Total Debt"
                              onChange={markOverridden("balance_to_totaldebt_percentage")}
                            />
                          </Form.Item>
                          <Form.Item label="Frequency" name={"frequency"}>
                            <Select
                              defaultValue={FREQUENCY_TYPES.WEEKLY}
                              options={Object.values(FREQUENCY_TYPES).map((val) => {
                                return { label: toTitleCase(val), value: val };
                              })}
                              placeholder="Frequency"
                            />
                          </Form.Item>
                        </div>
                        {/* <Form.Item label="Attachments">
          <div className="space-y-3">
            <div>
              <input type="file" id="file-input" multiple onChange={handleFileSelect} style={{ display: "none" }} />
              <Button icon={<TbPaperclip size={18} />} onClick={() => document.getElementById("file-input")?.click()}>
                Attach Files
              </Button>
            </div>

            {attachedFiles.length > 0 && (
              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <Typography.Text strong className="text-sm">
                    {attachedFiles.length} file(s) attached
                  </Typography.Text>
                </div>
                <div className="space-y-2">
                  {attachedFiles.map((file, index) => {
                    const type = getContentTypeFromFile(file);

                    return (
                      <div
                        key={index}
                        className={`flex items-center justify-between  p-2 rounded transition-colors bg-white`}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {type === "pdf" ? <TbFileTypePdf /> : null}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-gray-700 truncate">{file.name}</div>
                            <div className="text-xs text-gray-500">{formatBytes(file.size)}</div>
                          </div>
                        </div>
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<TbX size={16} />}
                          onClick={() => handleRemoveFile(index)}
                          className="flex-shrink-0"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </Form.Item> */}
                      </>
                    ),
                  },

                  // {
                  //   key: "2",
                  //   label: "Business Details",
                  //   onClick: () => {
                  //     setDefaultActiveKey(defaultActiveKey === "2" ? undefined : "2");
                  //   },
                  //   children: (
                  //     <>
                  //       <Form.Item label="Company Name" name={["business_info", "company_name"]}>
                  //         <Input placeholder="Company name" />
                  //       </Form.Item>
                  //       <div className="grid grid-cols-2 gap-4">
                  //         <Form.Item label="EIN Number" name={["business_info", "ein_number"]}>
                  //           <Input placeholder="EIN number" />
                  //         </Form.Item>
                  //         <Form.Item label="Business Type" name={["business_info", "business_type"]}>
                  //           <Input placeholder="Business type" />
                  //         </Form.Item>
                  //       </div>
                  //       <Form.Item label="Description" name={["business_info", "description"]}>
                  //         <Input.TextArea placeholder="Business description" rows={3} />
                  //       </Form.Item>
                  //       <div className="grid grid-cols-2 gap-4">
                  //         <Form.Item label="Phone" name={["business_info", "phone"]}>
                  //           <Input placeholder="Phone number" />
                  //         </Form.Item>
                  //         <Form.Item label="Email" name={["business_info", "email"]}>
                  //           <Input placeholder="Email address" type="email" />
                  //         </Form.Item>
                  //       </div>
                  //       <Form.Item label="Street Address" name={["business_info", "street_address"]}>
                  //         <Input placeholder="Street address" />
                  //       </Form.Item>
                  //       <div className="grid grid-cols-3 gap-4">
                  //         <Form.Item label="City" name={["business_info", "city"]}>
                  //           <Input placeholder="City" />
                  //         </Form.Item>
                  //         <Form.Item label="State" name={["business_info", "state"]}>
                  //           <Input placeholder="State" />
                  //         </Form.Item>
                  //         <Form.Item label="Zip Code" name={["business_info", "zip_code"]}>
                  //           <InputNumber placeholder="Zip code" style={{ width: "100%" }} />
                  //         </Form.Item>
                  //       </div>
                  //     </>
                  //   ),
                  // },

                  // {
                  //   key: "3",
                  //   label: "Client Details",
                  //   onClick: () => {
                  //     setDefaultActiveKey(defaultActiveKey === "3" ? undefined : "3");
                  //   },
                  //   children: (
                  //     <>
                  //       <Form.Item label="Client Name" name={["client_details", "name"]} rules={[{ required: true }]}>
                  //         <Input placeholder="Client name" />
                  //       </Form.Item>
                  //       <div className="grid grid-cols-2 gap-4">
                  //         <Form.Item label="SSN" name={["client_details", "ssn"]}>
                  //           <Input placeholder="SSN" />
                  //         </Form.Item>
                  //         <Form.Item label="Status" name={["client_details", "status"]}>
                  //           <Input placeholder="Status" />
                  //         </Form.Item>
                  //       </div>
                  //       <div className="grid grid-cols-2 gap-4">
                  //         <Form.Item label="Phone" name={["client_details", "phone"]}>
                  //           <Input placeholder="Phone number" />
                  //         </Form.Item>
                  //         <Form.Item label="Alternate Phone" name={["client_details", "alternate_phone"]}>
                  //           <InputNumber placeholder="Alternate phone" style={{ width: "100%" }} />
                  //         </Form.Item>
                  //       </div>
                  //       <Form.Item label="Street Address" name={["client_details", "street_address"]}>
                  //         <Input placeholder="Street address" />
                  //       </Form.Item>
                  //       <div className="grid grid-cols-3 gap-4">
                  //         <Form.Item label="City" name={["client_details", "city"]}>
                  //           <Input placeholder="City" />
                  //         </Form.Item>
                  //         <Form.Item label="State" name={["client_details", "state"]}>
                  //           <Input placeholder="State" />
                  //         </Form.Item>
                  //         <Form.Item label="Zip Code" name={["client_details", "zip_code"]}>
                  //           <InputNumber placeholder="Zip code" style={{ width: "100%" }} />
                  //         </Form.Item>
                  //       </div>
                  //       <div className="grid grid-cols-2 gap-4">
                  //         <Form.Item label="Weekly Budget" name={["client_details", "weekly_budget"]}>
                  //           <InputNumber
                  //             formatter={formatter}
                  //             parser={parser}
                  //             prefix="$"
                  //             style={{ width: "100%" }}
                  //             placeholder="Weekly budget"
                  //           />
                  //         </Form.Item>
                  //         <Form.Item label="Profit Margin (%)" name={["client_details", "profit_margin"]}>
                  //           <InputNumber
                  //             min={0}
                  //             max={100}
                  //             formatter={(value) => `${value}%`}
                  //             parser={(value: string | undefined) => Number(value?.replace("%", ""))}
                  //             style={{ width: "100%" }}
                  //             placeholder="Profit margin"
                  //           />
                  //         </Form.Item>
                  //       </div>
                  //     </>
                  //   ),
                  // },
                ]}
                defaultActiveKey={"1"}
                // activeKey={defaultActiveKey}
              />
            </Form>
          </div>
        </div>
        {canScrollDown && (
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 flex h-16 items-end justify-center pb-1"
            style={{ background: "linear-gradient(to top, #ffffff 35%, rgba(255, 255, 255, 0))" }}
          >
            <Button
              className="pointer-events-auto"
              size="small"
              shape="round"
              icon={<TbChevronDown />}
              iconPosition="end"
              onClick={scrollDown}
            >
              Scroll for more
            </Button>
          </div>
        )}
      </div>
      {source && source === "application-page" && isPlanSelected && !isPriorApplication && (
        <Alert
          icon={<TbAlertCircle size={20} />}
          className="mt-4"
          type="warning"
          message="Adding/editing a loan will reset your current selected strategy if application has not yet signed. Please re-select your preferred strategy after adding/editing the loan."
        />
      )}
    </Modal>
  );
};

export interface UploadLoanModalProps {
  isOpen: boolean;
  onClose: () => void;
  uploadType: "loan" | "bank" | "lawsuits" | "agreements";
  appkey: string;
  isPlanSelected?: boolean;
  isPriorApplication?: boolean;
}

export const UploadLoanModal = ({
  isOpen,
  onClose,
  uploadType,
  appkey,
  isPlanSelected,
  isPriorApplication = false,
}: UploadLoanModalProps) => {
  const addBank = ApiService.upstream.useAddBankByFile();
  const addLoan = ApiService.upstream.useAddLoanByFile();
  const addAgreement = ApiService.upstream.useAddAgreementFiles();
  // const addLawsuit = ApiService.upstream.useAddLawsuitByFile();
  const allowedExtensions = ["pdf", "doc", "docx"];
  const allowedMimeTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      const mismatchedFileTypes = fileArray.filter((file) => {
        // Primary check: MIME type
        if (file.type && allowedMimeTypes.includes(file.type)) {
          return false;
        }

        // Fallback: extension check (important for docs/xlsx)
        const ext = file.name.split(".").pop()?.toLowerCase();
        return !ext || !allowedExtensions.includes(ext);
      });

      if (mismatchedFileTypes.length > 0) {
        message.error("Only PDF and DOC files are allowed");
        return;
      }
      // console.log({ fileArray });
      const oversizedFiles = fileArray.filter((file) => file.size > 25 * 1024 * 1024); // 25MB limit

      if (oversizedFiles.length > 0) {
        message.error(`Some files exceed 25MB limit: ${oversizedFiles.map((f) => f.name).join(", ")}`);
        return;
      }

      setAttachedFiles((prev) => [...prev, ...fileArray]);
      // message.success(`${fileArray.length} file(s) attached`);
    }

    event.target.value = "";
  };
  const handleRemoveFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
    // message.success("File removed");
  };
  //onSubmit upload files to server first make it formData
  const onSubmit = () => {
    if (attachedFiles.length === 0) {
      message.error("Please attach at least one file");
      return;
    }

    const formData = new FormData();
    attachedFiles.forEach((file) => {
      formData.append("files", file);
    });

    //upload files to server
    if (uploadType === "bank") {
      addBank.mutate(
        { appkey, files: formData },
        {
          onSuccess: () => {
            message.success("Bank file processing has started!");
            setAttachedFiles([]);
            onClose();
            queryClient.invalidateQueries({ queryKey: ["application", appkey] });
          },
        },
      );
    } else if (uploadType === "loan") {
      addLoan.mutate(
        { appkey, files: formData },
        {
          onSuccess: () => {
            message.success("Loan file processing has started!");
            setAttachedFiles([]);
            onClose();
            queryClient.invalidateQueries({ queryKey: ["application", appkey] });
          },
        },
      );
    } else if (uploadType === "agreements") {
      addAgreement.mutate(
        { appkey, files: formData },
        {
          onSuccess: () => {
            message.success("Agreement files have been uploaded.");
            setAttachedFiles([]);
            onClose();
            queryClient.invalidateQueries({ queryKey: ["application", appkey] });
          },
        },
      );
    }

    // setAttachedFiles([]);
    // onClose();
  };
  return (
    <Modal
      width={600}
      destroyOnHidden
      open={isOpen}
      title={
        uploadType === "loan"
          ? "Upload Loan Docs"
          : uploadType === "bank"
            ? "Upload Bank Docs"
            : uploadType === "lawsuits"
              ? "Upload Lawsuits"
              : "Upload Agreements"
      }
      onCancel={() => {
        onClose();
        setAttachedFiles([]);
      }}
      onOk={onSubmit}
      okButtonProps={{
        disabled: attachedFiles.length === 0,
        loading: addBank.isPending || addLoan.isPending || addAgreement.isPending,
      }}
      okText="Upload"
    >
      <label
        htmlFor="uploadFile1"
        className="bg-white text-slate-500 font-semibold text-base rounded w-full h-52 flex flex-col items-center justify-center cursor-pointer border-2 border-gray-300 border-dashed mx-auto"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-11 mb-3 fill-gray-500" viewBox="0 0 32 32">
          <path
            d="M23.75 11.044a7.99 7.99 0 0 0-15.5-.009A8 8 0 0 0 9 27h3a1 1 0 0 0 0-2H9a6 6 0 0 1-.035-12 1.038 1.038 0 0 0 1.1-.854 5.991 5.991 0 0 1 11.862 0A1.08 1.08 0 0 0 23 13a6 6 0 0 1 0 12h-3a1 1 0 0 0 0 2h3a8 8 0 0 0 .75-15.956z"
            data-original="#000000"
          />
          <path
            d="M20.293 19.707a1 1 0 0 0 1.414-1.414l-5-5a1 1 0 0 0-1.414 0l-5 5a1 1 0 0 0 1.414 1.414L15 16.414V29a1 1 0 0 0 2 0V16.414z"
            data-original="#000000"
          />
        </svg>
        Upload file
        <input onChange={handleFileSelect} type="file" multiple id="uploadFile1" className="hidden" />
        <p className="text-xs font-medium text-slate-400 mt-2">PDF files are allowed.</p>
      </label>
      <div className="space-y-3 max-h-[320px] overflow-y-auto mt-4">
        {attachedFiles.length > 0 && (
          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <Typography.Text strong className="text-sm">
                {attachedFiles.length} file(s) attached
              </Typography.Text>
            </div>
            <div className="space-y-2">
              {attachedFiles.map((file, index) => {
                const type = getContentTypeFromFile(file);

                return (
                  <div
                    key={index}
                    className={`flex items-center justify-between  p-2 rounded transition-colors bg-white`}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {type === "pdf" ? <TbFileTypePdf /> : null}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-700 truncate">{file.name}</div>
                        <div className="text-xs text-gray-500">{formatBytes(file.size)}</div>
                      </div>
                    </div>
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<TbX size={16} />}
                      onClick={() => handleRemoveFile(index)}
                      className="flex-shrink-0"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {uploadType === "loan" && isPlanSelected && !isPriorApplication && (
        <Alert
          icon={<TbAlertCircle size={20} />}
          className="mt-4"
          type="warning"
          message="Adding/editing a loan will reset your current selected strategy if application has not yet signed. Please re-select your preferred strategy after adding/editing the loan."
        />
      )}
    </Modal>
  );
};

interface UploadLawSuits {
  isOpen: boolean;
  onClose: () => void;
  appkey: string;
  loanDetails?: T_Populate<
    LoanSchemaDto,
    {
      attachments: FileSchemaDto[];
    }
  >;
}

export const UploadLawSuitsModal = ({ isOpen, onClose, appkey, loanDetails }: UploadLawSuits) => {
  const addLawsuit = ApiService.upstream.useAddLawsuitByFile();
  const allowedExtensions = ["pdf", "doc", "docx"];
  const [status, setStatus] = useState("");
  const allowedMimeTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      const mismatchedFileTypes = fileArray.filter((file) => {
        // Primary check: MIME type
        if (file.type && allowedMimeTypes.includes(file.type)) {
          return false;
        }

        // Fallback: extension check (important for docs/xlsx)
        const ext = file.name.split(".").pop()?.toLowerCase();
        return !ext || !allowedExtensions.includes(ext);
      });

      if (mismatchedFileTypes.length > 0) {
        message.error("Only PDF and DOC files are allowed");
        return;
      }
      // console.log({ fileArray });
      const oversizedFiles = fileArray.filter((file) => file.size > 25 * 1024 * 1024); // 25MB limit

      if (oversizedFiles.length > 0) {
        message.error(`Some files exceed 25MB limit: ${oversizedFiles.map((f) => f.name).join(", ")}`);
        return;
      }

      setAttachedFiles((prev) => [...prev, ...fileArray]);
      // message.success(`${fileArray.length} file(s) attached`);
    }

    event.target.value = "";
  };
  const handleRemoveFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
    // message.success("File removed");
  };
  //onSubmit upload files to server first make it formData
  const onSubmit = () => {
    if (attachedFiles.length === 0) {
      message.error("Please attach at least one file");
      return;
    }

    const formData = new FormData();
    attachedFiles.forEach((file) => {
      formData.append("files", file);
    });
    formData.append("loan_id", loanDetails?._id || "");
    formData.append("loan_name", loanDetails?.creditor_name || "");
    formData.append("status", status);
    // console.log({ formData });
    // console.log({ attachedFiles });

    addLawsuit.mutate(
      { appkey, lawsuits: formData },
      {
        onSuccess: () => {
          message.success("Lawsuit uploaded successfully!");
          setAttachedFiles([]);
          onClose();
          queryClient.invalidateQueries({ queryKey: ["application", appkey] });
        },
      },
    );

    // setAttachedFiles([]);
    // onClose();
  };
  return (
    <Modal
      width={600}
      destroyOnHidden
      open={isOpen}
      title={"Upload Lawsuits"}
      onCancel={() => {
        onClose();
        setAttachedFiles([]);
      }}
      onOk={onSubmit}
      okButtonProps={{ disabled: attachedFiles.length === 0, loading: addLawsuit.isPending }}
      okText="Upload"
    >
      <label
        htmlFor="uploadFile1"
        className="bg-white text-slate-500 font-semibold text-base rounded w-full h-52 flex flex-col items-center justify-center cursor-pointer border-2 border-gray-300 border-dashed mx-auto"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-11 mb-3 fill-gray-500" viewBox="0 0 32 32">
          <path
            d="M23.75 11.044a7.99 7.99 0 0 0-15.5-.009A8 8 0 0 0 9 27h3a1 1 0 0 0 0-2H9a6 6 0 0 1-.035-12 1.038 1.038 0 0 0 1.1-.854 5.991 5.991 0 0 1 11.862 0A1.08 1.08 0 0 0 23 13a6 6 0 0 1 0 12h-3a1 1 0 0 0 0 2h3a8 8 0 0 0 .75-15.956z"
            data-original="#000000"
          />
          <path
            d="M20.293 19.707a1 1 0 0 0 1.414-1.414l-5-5a1 1 0 0 0-1.414 0l-5 5a1 1 0 0 0 1.414 1.414L15 16.414V29a1 1 0 0 0 2 0V16.414z"
            data-original="#000000"
          />
        </svg>
        Upload file
        <input onChange={handleFileSelect} type="file" multiple id="uploadFile1" className="hidden" />
        <p className="text-xs font-medium text-slate-400 mt-2">PDF files are allowed.</p>
      </label>
      {/* <Form.Item className="mt-2" layout="vertical" label="Lawsuit Status">
        <Select
          onChange={(val) => setStatus(val)}
          className="!w-full"
          placeholder="Select status"
          options={Object.values(LawSuitTypes).map((el, i) => {
            return { label: toTitleCase(el, { sep: "_" }), value: el };
          })}
        />
      </Form.Item> */}
      <div className="space-y-3 max-h-[320px] overflow-y-auto mt-4">
        {attachedFiles.length > 0 && (
          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <Typography.Text strong className="text-sm">
                {attachedFiles.length} file(s) attached
              </Typography.Text>
            </div>
            <div className="space-y-2">
              {attachedFiles.map((file, index) => {
                const type = getContentTypeFromFile(file);

                return (
                  <div
                    key={index}
                    className={`flex items-center justify-between  p-2 rounded transition-colors bg-white`}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {type === "pdf" ? <TbFileTypePdf /> : null}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-700 truncate">{file.name}</div>
                        <div className="text-xs text-gray-500">{formatBytes(file.size)}</div>
                      </div>
                    </div>
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<TbX size={16} />}
                      onClick={() => handleRemoveFile(index)}
                      className="flex-shrink-0"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

interface BankModalProps {
  open: boolean;
  onClose: () => void;
  selectedBank?: T_Populate<
    BankSchemaDto,
    {
      attachments: FileSchemaDto[];
    }
  >;
  appkey: string;
}
{
  /**export enum BankStatusType {
  ACTIVE = "active",
  INACTIVE = "inactive",
  CLOSED = "closed",
  SUSPENDED = "suspended",
  UNKNOWN = "unknown",
}

export type BankDto = {
  user: T_MongoId;
  name?: string;
  account_number?: string;
  current_balance?: number;
  transactions: {
    value_date: Date;
    type: TransactionType;
    value: number;
    remarks?: string;
  }[];
  status: BankStatusType;
  attachments: T_MongoId[];
}; */
}

export const BankModal = ({ onClose, open, selectedBank, appkey }: BankModalProps) => {
  const editBank = ApiService.upstream.useEditBank();
  const addBank = ApiService.upstream.useAddBank();
  const formatter = (value: number | undefined) => {
    if (value === undefined) return "";
    const [start, end] = `${value}`.split(".") || [];
    const v = `${start}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return `${end ? `${v}.${end}` : `${v}`}`;
  };
  const parser = (value: string | undefined) => value?.replace(/\$\s?|(,*)/g, "") as unknown as number;
  const [bankForm] = Form.useForm();
  // console.log({ inside: selectedBank });

  useEffect(() => {
    if (selectedBank) {
      bankForm.setFieldsValue({
        ...selectedBank,
        transactions: selectedBank.transactions?.map((tx) => ({
          ...tx,
          value_date: tx.value_date ? dayjs(tx.value_date) : undefined,
        })),
      });
    } else {
      bankForm.resetFields();
    }
  }, [selectedBank, bankForm]);

  return (
    <Modal
      title={selectedBank?.name ?? "Add Bank Details"}
      open={open}
      okText={selectedBank ? "Update Details" : "Add Details "}
      onCancel={onClose}
      onOk={() => bankForm.submit()}
      width={800}
      centered
      styles={{
        body: {
          height: 600,
          overflowY: "auto",
        },
        content: {
          padding: 24,
        },
      }}
    >
      <Form
        form={bankForm}
        layout="vertical"
        onFinish={(values) => {
          const payload = {
            ...values,
            transactions: values.transactions?.map((tx: any) => ({
              ...tx,
              value_date: tx.value_date ? tx.value_date.toDate() : undefined,
            })),
          };

          if (selectedBank) {
            editBank.mutate(
              { appkey, bank: payload },
              {
                onSuccess: () => {
                  message.success("Bank edited succesfully!");
                  onClose();
                  queryClient.invalidateQueries({ queryKey: ["application", appkey] });
                },
              },
            );
          } else {
            addBank.mutate(
              { appkey, bank: payload },
              {
                onSuccess: () => {
                  message.success("Bank added succesfully!");
                  onClose();
                  queryClient.invalidateQueries({ queryKey: ["application", appkey] });
                },
              },
            );
          }
        }}
      >
        <Form.Item hidden name={"_id"}></Form.Item>
        <Form.Item label="Bank Name" name={"name"}>
          <Input placeholder="Bank name" />
        </Form.Item>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item label="Account Number" name={"account_number"}>
            <Input disabled={!!selectedBank} placeholder="Account number" />
          </Form.Item>
          <Form.Item label="Current Balance" name={"current_balance"}>
            <InputNumber
              formatter={formatter}
              parser={parser}
              prefix="$"
              style={{ width: "100%" }}
              placeholder="Current balance"
            />
          </Form.Item>
        </div>

        <Form.Item label="Status" name={"status"} rules={[{ required: true, message: "Status is required" }]}>
          <Select
            placeholder="Select status"
            options={Object.values(BankStatusType).map((val) => ({ label: toTitleCase(val), value: val }))}
          />
        </Form.Item>

        <Typography.Title level={5} className="!mb-3">
          Transactions
        </Typography.Title>

        <Form.List name="transactions">
          {(fields, { add, remove }) => (
            <>
              {fields.length === 0 ? (
                <div className="border border-dashed border-gray-300 rounded-lg p-4 mb-3">
                  <Typography.Text type="secondary" className="block text-center mb-3">
                    No transactions added
                  </Typography.Text>
                  <Button type="dashed" onClick={() => add()} block>
                    + Add Transaction
                  </Button>
                </div>
              ) : (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <div key={key} className="border border-solid border-gray-200 rounded-lg p-4 mb-3 bg-gray-50">
                      <div className="flex justify-between items-center mb-3">
                        <Typography.Text strong>Transaction {index + 1}</Typography.Text>
                        <Button type="text" danger size="small" onClick={() => remove(name)}>
                          Remove
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <Form.Item
                          {...restField}
                          label="Value Date"
                          name={[name, "value_date"]}
                          rules={[{ required: true, message: "Date is required" }]}
                        >
                          <DatePicker className="py-2 !w-full" type="date" placeholder="Value date" />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          label="Type"
                          name={[name, "type"]}
                          rules={[{ required: true, message: "Type is required" }]}
                        >
                          <Select
                            placeholder="Select type"
                            options={Object.values(TransactionType).map((val) => ({
                              label: toTitleCase(val),
                              value: val,
                            }))}
                          />
                        </Form.Item>
                      </div>

                      <Form.Item
                        {...restField}
                        label="Value"
                        name={[name, "value"]}
                        rules={[{ required: true, message: "Value is required" }]}
                      >
                        <InputNumber
                          formatter={formatter}
                          parser={parser}
                          prefix="$"
                          style={{ width: "100%" }}
                          placeholder="Transaction value"
                        />
                      </Form.Item>
                    </div>
                  ))}

                  <Button type="dashed" onClick={() => add()} block className="mb-3">
                    + Add Transaction
                  </Button>
                </>
              )}
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

export const BusinessInfoUpdateModal = ({
  isOpen,
  onClose,
  businessInfo,
  clientDetails,
  selectedBank,
  appKey,
}: {
  appKey: string;
  isOpen: boolean;
  onClose: () => void;
  businessInfo: BusinessInfo | null;
  clientDetails: ClientDetails | null;
  selectedBank: SelectedBank | null;
}) => {
  const editMetaInfo = ApiService.upstream.useEditMetaInfo();
  const autoCompleteOption: { label: string; value: string }[] = stateNames.map((state) => {
    return { label: state, value: state };
  });
  const [options, setOptions] = useState<{ label: string; value: string }[] | undefined>(autoCompleteOption);
  const [form] = Form.useForm();
  useEffect(() => {
    if (businessInfo && clientDetails) {
      form.setFieldsValue({
        business_info: {
          ...businessInfo,
          ein_number: businessInfo.ein_number ? businessInfo.ein_number?.split("-").join("") : "",
        },
        client_details: {
          ...clientDetails,
          ssn: clientDetails.ssn ? clientDetails.ssn?.split("-").join("") : "",
        },
        selected_bank: selectedBank
          ? {
              ...selectedBank,
            }
          : {},
      });
    } else {
      form.resetFields();
    }
  }, [businessInfo, clientDetails, selectedBank, form]);

  return (
    <Modal
      centered
      width={800}
      open={isOpen}
      onCancel={onClose}
      onOk={() => form.submit()}
      title="Personal Information"
      styles={{
        body: {
          height: 600,
          overflowY: "auto",
        },
        content: {
          paddingInline: 0,
        },
        header: {
          paddingInline: 24,
        },
        footer: {
          paddingInline: 24,
        },
      }}
    >
      <div className="h-[600px] overflow-y-auto">
        <Form
          onFinish={(values) => {
            editMetaInfo.mutate(
              {
                appkey: appKey,
                business_info: values.business_info,
                client_details: values.client_details,
                selected_bank: values.selected_bank,
              },
              {
                onSuccess: () => {
                  message.success(`Personal information edited succesfully!`);
                  onClose();
                  form.resetFields();
                  queryClient.invalidateQueries({ queryKey: ["application", appKey] });
                },
              },
            );
          }}
          form={form}
          layout="vertical"
        >
          <div className="flex flex-col gap-8 max-w-[900px] mx-auto p-5">
            <div className="bg-white border border-solid border-gray-300 rounded-xl overflow-auto">
              <div className="flex justify-between items-center px-6 py-4 border-[0] border-b border-solid border-gray-300 ">
                <Typography.Title level={4} className="!m-0">
                  Customer Details
                </Typography.Title>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-12">
                    <Form.Item name={["client_details", "name"]} label="Full Name" rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                  </div>
                </div>

                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-6">
                    <Form.Item
                      name={["client_details", "ssn"]}
                      label="SSN"
                      rules={[
                        { required: true, pattern: new RegExp(/^\d+$/), message: "Only digits are allowed" },
                        { len: 9, message: "SSN must be 9 digits" },
                      ]}
                    >
                      <Input maxLength={9} minLength={9} />
                    </Form.Item>
                  </div>
                  <div className="col-span-6">
                    <Form.Item name={["client_details", "phone"]} label="Mobile No." rules={[{ required: true }]}>
                      <InputNumber<string>
                        type="tel"
                        minLength={14}
                        maxLength={14}
                        style={{ width: "100%" }}
                        formatter={(value) => {
                          if (!value) return "";
                          const onlyNumbers = `${value}`.replace(/\D/g, "");
                          const formatted = onlyNumbers.replace(
                            /^(\d{0,3})(\d{0,3})(\d{0,4})$/,
                            (match, p1, p2, p3) => `${p1 ? `(${p1}) ` : ""}${p2 ? `${p2}` : ""}${p3 ? `-${p3}` : ""}`,
                          );
                          return formatted.trim();
                        }}
                        parser={(value) => value?.replace(/\D/g, "") as unknown as string}
                        controls={false}
                      />
                    </Form.Item>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-8 max-w-[900px] mx-auto p-5">
            <div className="bg-white border border-solid border-gray-300 rounded-xl overflow-auto">
              <div className="flex justify-between items-center px-6 py-4  border-[0] border-b border-solid border-gray-300 ">
                <Typography.Title level={4} className="!m-0">
                  Business Information
                </Typography.Title>
              </div>
              <div className="p-5">
                <Form.Item name={["business_info", "company_name"]} label="Company Name" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-6">
                    <Form.Item
                      name={["business_info", "email"]}
                      label="Email"
                      rules={[{ required: true, type: "email" }]}
                    >
                      <Input inputMode="email" />
                    </Form.Item>
                  </div>
                  <div className="col-span-6">
                    <Form.Item name={["business_info", "phone"]} label="Phone" rules={[{ required: true }]}>
                      <InputNumber<string>
                        type="tel"
                        minLength={14}
                        maxLength={14}
                        style={{ width: "100%" }}
                        formatter={(value) => {
                          if (!value) return "";
                          const onlyNumbers = `${value}`.replace(/\D/g, "");
                          const formatted = onlyNumbers.replace(
                            /^(\d{0,3})(\d{0,3})(\d{0,4})$/,
                            (match, p1, p2, p3) => `${p1 ? `(${p1}) ` : ""}${p2 ? `${p2}` : ""}${p3 ? `-${p3}` : ""}`,
                          );
                          return formatted.trim();
                        }}
                        parser={(value) => value?.replace(/\D/g, "") as unknown as string}
                        controls={false}
                      />
                    </Form.Item>
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-6">
                    <Form.Item
                      name={["business_info", "business_type"]}
                      label="Business Type"
                      rules={[{ required: true }]}
                    >
                      <Select
                        options={[
                          "Corporation",
                          "Limited Liability Company",
                          "Partnership",
                          "Limited Partnership",
                          "Limited Liability Partnership",
                          "Sole Proprietorship",
                          "Other",
                        ].map((el) => ({ label: el, value: el }))}
                      />
                    </Form.Item>
                  </div>
                  <div className="col-span-6">
                    <Form.Item
                      name={["business_info", "ein_number"]}
                      label="EIN Number"
                      rules={[
                        { required: true, pattern: new RegExp(/^\d+$/), message: "Only digits are allowed" },
                        { len: 9, message: "EIN must be 9 digits" },
                      ]}
                    >
                      <Input maxLength={9} minLength={9} />
                    </Form.Item>
                  </div>
                  {/* <div className="col-span-4">
                          <Form.Item
                            name={"business_type"}
                            label="Type of Business"
                            rules={[{ required: true }]}
                          >
                            <Input />
                          </Form.Item>
                        </div> */}
                </div>

                <Divider></Divider>
                <Form.Item
                  name={["business_info", "street_address"]}
                  label="Street Address"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-4">
                    <Form.Item name={["business_info", "city"]} label="City" rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                  </div>
                  <div className="col-span-4">
                    <Form.Item name={["business_info", "state"]} label="State" rules={[{ required: true }]}>
                      <AutoComplete
                        options={options}
                        style={{ width: "100%" }}
                        onBlur={() => setOptions(autoCompleteOption)}
                        onFocus={() => setOptions(autoCompleteOption)}
                        // onSelect={onSelect}
                        onSearch={(text) => {
                          setOptions(
                            autoCompleteOption.filter((option) => option.value.toLowerCase().startsWith(text)),
                          );
                        }}
                        placeholder="Search state here"
                      />
                    </Form.Item>
                  </div>

                  <div className="col-span-4">
                    <Form.Item name={["business_info", "zip_code"]} label="ZIP Code" rules={[{ required: true }]}>
                      <Input minLength={5} maxLength={5} />
                    </Form.Item>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-8 max-w-[900px] mx-auto p-5">
            <div className="bg-white border border-solid border-gray-300 rounded-xl overflow-auto">
              <Form.List name={["client_details", "cosigners"]}>
                {(fields, { add, remove }) => (
                  <>
                    <div className="flex justify-between items-center px-6 py-4  border-[0] border-b border-solid border-gray-300 ">
                      <Typography.Title level={4} className="!m-0">
                        Cosigner Details
                      </Typography.Title>
                      {fields.length === 0 ? (
                        <Button onClick={() => add()} icon={<TbPlus />}>
                          Add Cosigner
                        </Button>
                      ) : (
                        <Button onClick={() => remove(0)} icon={<TbMinus />}>
                          Remove Cosigner
                        </Button>
                      )}
                    </div>
                    <div className="p-5">
                      {fields.length === 0 ? (
                        <Empty description="Don't have cosigner" image={Empty.PRESENTED_IMAGE_DEFAULT} />
                      ) : null}
                      {fields.map(({ key, name, ...restField }) => (
                        <Fragment key={key}>
                          <Form.Item
                            {...restField}
                            label="Cosigner Name"
                            name={[name, "name"]}
                            rules={[{ required: true, message: "Missing cosigner name" }]}
                          >
                            <Input placeholder="Name" />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            label="Cosigner Email"
                            name={[name, "email"]}
                            rules={[
                              { required: true, message: "Missing cosigner email" },
                              { type: "email", message: "Invalid email" },
                            ]}
                          >
                            <Input placeholder="Email" />
                          </Form.Item>
                        </Fragment>
                      ))}
                    </div>
                  </>
                )}
              </Form.List>
            </div>
          </div>

          <div className="flex flex-col gap-8 max-w-[900px] mx-auto p-5 mb-[98px]">
            <div className="bg-white border border-solid border-gray-300 rounded-xl overflow-auto">
              <div className="flex justify-between items-center px-6 py-4 border-[0] border-b border-solid border-gray-300 ">
                <Typography.Title level={4} className="!m-0">
                  Payment Information
                </Typography.Title>
              </div>
              <div className="p-5">
                <Form.Item name={["selected_bank", "bank_name"]} label="Bank Name" rules={[{ required: true }]}>
                  <Input style={{ width: "100%" }} />
                </Form.Item>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-6">
                    <Form.Item
                      name={["selected_bank", "name_on_account"]}
                      label="Name on account"
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>
                  </div>
                  <div className="col-span-6">
                    <Form.Item name={["selected_bank", "routing_no"]} label="Routing No" rules={[{ required: true }]}>
                      <Input maxLength={9} minLength={9} />
                    </Form.Item>
                  </div>
                </div>
                <Form.Item name={["selected_bank", "account_no"]} label="Account No" rules={[{ required: true }]}>
                  <Input style={{ width: "100%" }} />
                </Form.Item>
              </div>
            </div>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export function GraphLoader({
  application,
  hide = false,
}: {
  application?: T_Populate<
    GetClearApplicationDto,
    {
      loans: T_Populate<LoanSchemaDto, { attachments: FileSchemaDto[] }>[];
      banks: T_Populate<BankSchemaDto, { attachments: FileSchemaDto[] }>[];
      lawsuits: T_Populate<LawSuitSchemaDto, { attachments: FileSchemaDto[] }>[];
      agreements: FileSchemaDto[];
      statement_files: FileSchemaDto[];
    }
  >;
  hide?: boolean;
  // strategy?: any;
}) {
  const loans = ApiService.onboarding.useGetArtifacts(application?.app_key as string, "loans");
  const graphs = ApiService.onboarding.useGetGraphs(application?.app_key as string);
  const debtorSummaryQuery = ApiService.onboarding.useGetDebtorSummary(application?.app_key as string);
  // console.log({ mainAmount: strategy?.main_payment_amount });

  const [isPaymentTableOpen, setIsPaymentTableOpen] = useState(false);

  const lowestWeeklyPaymentByUs = (application?.summary?.current_debt ?? 0) * WEEKLY_PAYMENTS_REDUCTION_MULTIPLIER;
  const withWeeklyPaymentByOthers = (application?.summary.weekly_payments ?? 0) * 0.55;
  const weeklyPaymemtsAsLowAs = {
    us: lowestWeeklyPaymentByUs,
    you: (application?.summary?.weekly_payments ?? 0) * 0.7,
    others:
      lowestWeeklyPaymentByUs > withWeeklyPaymentByOthers
        ? (application?.summary?.current_debt ?? 0) * 0.04
        : withWeeklyPaymentByOthers,
  };

  const weeklyCreditPie: { id: string; label: string; value: number; color: string }[] = debtorSummaryQuery?.data
    ?.result?.weekly_credits.totals
    ? Object.entries(debtorSummaryQuery?.data?.result?.weekly_credits.totals)?.map(([dayName, wcd], i) => ({
        id: String(i + 1),
        label: dayName,
        value: wcd.percentage,
        color: CHART_COLORS[i],
      }))
    : [];

  const withdrawal_percentage = debtorSummaryQuery.data?.result.withdrawal_percentage ?? {};
  //catch only 500 error of the above apis
  // if (loans.isError && loans.error.status === 500) {
  //   console.log({ err: loans.error });
  //   return (
  //     <div className="p-5">
  //       <Result status="error" title="Error loading loan data" subTitle="Please try again later." />
  //     </div>
  //   );
  // }
  // if (graphs.isError && graphs.error.status === 500) {
  //   console.log({ err: graphs.error });
  //   return (
  //     <div className="p-5">
  //       <Result status="error" title="Error loading graph data" subTitle="Please try again later." />
  //     </div>
  //   );
  // }
  // if (debtorSummaryQuery.isError && debtorSummaryQuery.error.status === 500) {
  //   console.log({ err: debtorSummaryQuery.error });
  //   return (
  //     <div className="p-5">
  //       <Result status="error" title="Error loading debtor summary data" subTitle="Please try again later." />
  //     </div>
  //   );
  // }

  if (hide) {
    return (
      <Empty
        className="mt-4"
        image={Empty.PRESENTED_IMAGE_DEFAULT}
        description="APR for Plaid applications are coming soon."
      />
    );
  }

  return (
    <div className="p-5 flex flex-col gap-3 mb-[98px]">
      {/* <div className="progress_loader w-full" /> */}
      <SingleQueryWrapper
        query={loans}
        renderData={(applicationLoans) => {
          const totalFundedAmount =
            applicationLoans.result.reduce((prev, curr) => prev + (curr.funded_amount ?? 0), 0) ?? 0;
          const totalPaybackAmount =
            applicationLoans.result.reduce((prev, curr) => prev + (curr.payback_amount ?? 0), 0) ?? 0;

          const currentAPR = calculateAPR(
            totalFundedAmount,
            application?.summary?.weekly_payments ?? 0,
            FREQUENCY_TYPES.WEEKLY,
            totalPaybackAmount / (application?.summary.weekly_payments ?? 1),
          );

          const expectedAPR = calculateAPR(
            totalFundedAmount,
            application?.strategy?.weekly_payments ?? 1,
            FREQUENCY_TYPES.WEEKLY,
            totalPaybackAmount / (application?.strategy?.weekly_payments ?? 1),
          );
          const reductionAPR = (currentAPR - expectedAPR) / currentAPR;
          // const currentIRR =
          //   calculateIRR(totalFundedAmount, totalPaybackAmount, application.summary.weekly_payments, "weekly").irr ?? 0;
          // const expectedIRR =
          //   calculateIRR(totalFundedAmount, totalPaybackAmount, strategy.sub_payment_amount, "weekly").irr ?? 0;
          // const reductionIRR = (currentIRR - expectedIRR) / currentIRR;

          const currentWeeklyPayment = application?.summary?.weekly_payments ?? 1;
          const expectedWeeklyPayment = application?.strategy?.weekly_payments ?? 0;
          const reductionWeeklyPayment = (currentWeeklyPayment - expectedWeeklyPayment) / currentWeeklyPayment;

          const totalMoneySavedByUs =
            debtorSummaryQuery.data?.result?.debtsmart_returns?.[
              debtorSummaryQuery.data?.result?.debtsmart_returns.length - 1
            ].debtsmart_return;

          return (
            <div className="flex gap-3">
              {/* <div className="bg-black ">
                <embed
                  className="col-span-3"
                  src="https://www.youtube.com/embed/HX7P99rW2Vc?autoplay=1&mute=1&loop=1&playlist=HX7P99rW2Vc"
                  style={{ aspectRatio: "9 / 16" }}
                  width="315"
                  height="560"
                />
              </div> */}
              <div className="flex flex-col flex-1 gap-3">
                <div className="grid grid-cols-[4fr_2fr] gap-3">
                  <AppCard className="grid grid-cols-[2fr] gap-3 flex-1 items-center">
                    <div className="space-y-2">
                      <div className="flex gap-1 justify-between">
                        <Typography.Text type="secondary">Current Weekly Payment</Typography.Text>
                        <Typography.Text className="font-semibold">{formatMoney(currentWeeklyPayment)}</Typography.Text>
                      </div>
                      <div className="flex gap-1 justify-between">
                        <Typography.Text type="secondary">Modified Weekly Payment</Typography.Text>
                        <Typography.Text className="font-semibold">
                          {formatMoney(expectedWeeklyPayment)}
                        </Typography.Text>
                      </div>
                    </div>
                    {currentWeeklyPayment === 0 ? null : (
                      <div className="flex flex-col items-center justify-center gap-2">
                        <div className="inline-flex items-center">
                          <Typography.Title level={2} className="!m-0">
                            {formatPercent(reductionWeeklyPayment)}
                          </Typography.Title>
                          <TbCaretDownFilled className="fill-green-600 size-10" />
                        </div>
                      </div>
                    )}
                  </AppCard>
                  <AppCard className="grid grid-cols-[2fr] gap-3 items-center">
                    <div className="flex flex-col gap-1">
                      <div className="flex justify-between">
                        <Typography.Text type="secondary">Current APR</Typography.Text>
                        <Typography.Text className="font-semibold">{formatNumber(currentAPR)}%</Typography.Text>
                      </div>
                      {/* <div className="flex justify-between">
                        <Typography.Text type="secondary">Modified APR</Typography.Text>
                        <Typography.Text className="font-semibold">{formatNumber(expectedAPR)}%</Typography.Text>
                      </div> */}
                    </div>
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="inline-flex items-center">
                        <Typography.Title level={2} className="!m-0">
                          {formatPercent(reductionAPR)}
                        </Typography.Title>
                        <TbCaretDownFilled className="fill-green-600 size-10" />
                      </div>
                    </div>
                  </AppCard>

                  {/* {isPaymentTableOpen ? (
                    <PaymentTable
                      application={application}
                      isPaymentTableOpen={isPaymentTableOpen}
                      setIsPaymentTableOpen={setIsPaymentTableOpen}
                      strategy={strategy}
                    />
                  ) : null} */}
                </div>
                {debtorSummaryQuery.isError ? null : (
                  <div className="w-full flex-1">
                    <AppCard className="p-6 flex flex-col mx-auto h-full">
                      {debtorSummaryQuery.isLoading ? (
                        loader
                      ) : debtorSummaryQuery.data?.result?.original_vs_optimal ? (
                        <>
                          <Typography.Title level={5} className="!m-0">
                            Current Payment v/s Optimal Payment
                          </Typography.Title>
                          <Divider />
                          <div style={{ width: "100%", height: 300 }}>
                            <ReOriginalOptimalBarChart
                              data={debtorSummaryQuery.data?.result?.original_vs_optimal}
                              // indexBy="id"
                              // keys={["original_amount", "optimal_amount"]}
                              // legend={{ x: "", y: "Amount" }}
                              // groupMode="grouped"
                            />
                          </div>
                        </>
                      ) : graphs?.data?.result?.external?.charts?.fundedAmountByCreditor ? (
                        <>
                          <Typography.Title level={5} className="!m-0">
                            Funded Amount By Creditor
                          </Typography.Title>
                          <Divider />
                          <div style={{ width: "100%", height: 300 }}>
                            <BarChart
                              data={graphs.data?.result?.external?.charts?.fundedAmountByCreditor}
                              indexBy="creditor"
                              keys={["fundedAmount"]}
                              legend={{ x: "Creditor", y: "Funded Amount" }}
                              groupMode="grouped"
                            />
                          </div>
                        </>
                      ) : null}
                    </AppCard>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  {debtorSummaryQuery.isError ? null : (
                    <div className="w-full flex-1">
                      <AppCard className="p-6 flex flex-col mx-auto h-full">
                        {debtorSummaryQuery.isLoading ? (
                          loader
                        ) : debtorSummaryQuery.data?.result?.debtsmart_returns ? (
                          <>
                            {/* <Typography.Title level={5} className="!m-0">
                        Bank Account Based Monthly Cashflow
                      </Typography.Title>
                      <Divider />
                      <div style={{ width: "100%", height: 400 }}>
                        <LineChartMultiple
                          data={graphs.data?.result?.cash_flow}
                          legend={{ x: "Bank Account Based Monthly Cashflow", y: "Cashflow Amount" }}
                        />
                      </div> */}
                            <div className="flex gap-3 justify-between items-center">
                              <Typography.Title level={5} className="!m-0">
                                Total Benefit
                              </Typography.Title>
                              <div className="inline-flex gap-1 items-center">
                                <Typography.Title level={3} className="!m-0">
                                  {formatMoney(totalMoneySavedByUs)}
                                </Typography.Title>
                                <Typography.Text type="secondary">in additional cashflow</Typography.Text>
                              </div>
                            </div>
                            <Divider />
                            <div style={{ width: "100%", height: 300 }}>
                              <LineChartFilled
                                data={debtorSummaryQuery.data?.result?.debtsmart_returns.map((el) => ({
                                  x: el.id,
                                  y: el.debtsmart_return,
                                }))}
                                legend={{ x: "", y: "Cashflow Saved" }}
                              />
                            </div>
                            <Typography.Text type="secondary" className="italic text-center block">
                              Cashflow re-investable over time
                            </Typography.Text>
                          </>
                        ) : graphs?.data?.result?.external?.charts?.fundedVsPaybackAmount ? (
                          <>
                            <Typography.Title level={5} className="!m-0">
                              Funded Amount vs Payback Amount
                            </Typography.Title>
                            <Divider />
                            <div style={{ width: "100%", height: 300 }}>
                              <BarChart
                                data={graphs.data?.result?.external?.charts?.fundedVsPaybackAmount}
                                indexBy="creditor"
                                keys={["fundedAmount", "paybackAmount"]}
                                groupMode="grouped"
                                colorTheme="dark2"
                              />
                            </div>
                          </>
                        ) : null}
                      </AppCard>
                    </div>
                  )}
                  <div className="flex flex-col gap-3">
                    <div className="w-full flex-1 h-full">
                      <AppCard className="p-6 flex flex-col mx-auto h-full">
                        <div className="flex gap-3 justify-between items-center">
                          <Typography.Title level={5} className="!m-0">
                            Total debt
                          </Typography.Title>
                          <div className="inline-flex gap-1 items-center">
                            <Typography.Title level={3} className="!m-0">
                              {formatMoney(application?.summary.current_debt ?? 0)}
                            </Typography.Title>
                          </div>
                        </div>
                        {/* {loans.isLoading || application.isLoading ? (
                      loader
                    ) : ( */}
                        <div style={{ width: "100%" }}>
                          <LoanPieChart
                            loanData={loans.data?.result}
                            currentDebt={application?.summary?.current_debt}
                          />
                        </div>
                        {/* )} */}
                      </AppCard>
                    </div>
                  </div>
                  {debtorSummaryQuery.isError ? null : (
                    <AppCard>
                      <Typography.Title level={5} className="!m-0">
                        By Restructuring You Save
                      </Typography.Title>
                      <Divider />
                      {debtorSummaryQuery.isLoading ? (
                        loader
                      ) : debtorSummaryQuery.data?.result?.debtsmart_returns && totalMoneySavedByUs ? (
                        // <div className="grid grid-cols-2 gap-2">
                        //   <Typography.Text type="secondary">Us</Typography.Text>
                        //   <Typography.Text className="font-semibold">{formatMoney(totalMoneySavedByUs)}</Typography.Text>
                        //   <Typography.Text type="secondary">You</Typography.Text>
                        //   <Typography.Text className="font-semibold">
                        //     {formatMoney(totalMoneySavedByUs * 0.7051)}
                        //   </Typography.Text>
                        //   <Typography.Text type="secondary">Others</Typography.Text>
                        //   <Typography.Text className="font-semibold">
                        //     {formatMoney(totalMoneySavedByUs * 0.5128)}
                        //   </Typography.Text>
                        // </div>
                        <div style={{ width: "100%", height: 300 }}>
                          <ReAmountHorizontalBarChart
                            data={[
                              {
                                id: "With us",
                                amount: totalMoneySavedByUs,
                              },
                              {
                                id: "Done by yourself",
                                amount: totalMoneySavedByUs * 0.7051,
                              },
                              {
                                id: "Done by others",
                                amount: totalMoneySavedByUs * 0.5128,
                              },
                            ]}
                            labelKey="id"
                            valueKey={"amount"}
                          />
                        </div>
                      ) : null}
                    </AppCard>
                  )}

                  <AppCard>
                    <Typography.Title level={5} className="!m-0">
                      Weekly Payments as low as
                    </Typography.Title>
                    <Divider />
                    {/* <div className="grid grid-cols-2 gap-2">
                      <Typography.Text type="secondary">Us</Typography.Text>
                      <Typography.Text className="font-semibold">
                        {formatMoney(strategy?.common?.weekly_payments)}
                      </Typography.Text>
                      <Typography.Text type="secondary">You</Typography.Text>
                      <Typography.Text className="font-semibold">
                        {formatMoney(weeklyPaymemtsAsLowAs.you)}
                      </Typography.Text>
                      <Typography.Text type="secondary">Others</Typography.Text>
                      <Typography.Text className="font-semibold">
                        {formatMoney(weeklyPaymemtsAsLowAs.others)}
                      </Typography.Text>
                    </div> */}
                    <div style={{ width: "100%", height: 300 }}>
                      <ReAmountVerticalBarChart
                        data={[
                          {
                            id: "With us",
                            amount: application?.strategy?.weekly_payments ?? 0,
                          },
                          {
                            id: "Done by yourself",
                            amount: weeklyPaymemtsAsLowAs.you,
                          },
                          {
                            id: "Done by others",
                            amount: weeklyPaymemtsAsLowAs.others,
                          },
                        ]}
                        labelKey="id"
                        valueKey={"amount"}
                      />
                    </div>
                  </AppCard>
                </div>

                {/* <AppCard>
                    <Typography.Title level={5} className="!m-0">
                      Category wise total transactions
                    </Typography.Title>
                    <Divider />
                    {debtorSummaryQuery.isLoading ? (
                      loader
                    ) : debtorSummaryQuery.data?.result?.debtor_summary?.category_recurrence_analysis ? (
                      <div style={{ width: "100%", height: 300 }}>
                        <ReCategorisedHorizontalBarChart
                          data={[
                            ...debtorSummaryQuery.data.result.debtor_summary.category_recurrence_analysis.map((el) => {
                              return { category: el.category, number: el.total_transactions };
                            }),
                          ].sort((a, b) => b.number - a.number)}
                          labelKey="category"
                          valueKey={"number"}
                        />
                      </div>
                    ) : null}
                  </AppCard> */}

                {debtorSummaryQuery?.data ? (
                  <CategorisedCreditTransactions
                    // categorisedCreditTransactions={debtorSummaryQuery?.data?.result?.categorised_credit_transactions}
                    debtorSummary={debtorSummaryQuery?.data?.result}
                    appKey={application?.app_key}
                  />
                ) : debtorSummaryQuery.isError ? null : (
                  loader
                )}

                {debtorSummaryQuery.isError ? null : (
                  <AppCard>
                    <Typography.Title level={5} className="!m-0">
                      Month wise profit vs profit w/o MCA
                    </Typography.Title>
                    <Divider />
                    {debtorSummaryQuery.isLoading ? (
                      loader
                    ) : debtorSummaryQuery.data?.result?.month_wise_profit ? (
                      <div style={{ width: "100%", height: 300 }}>
                        <ReProfitVerticalBarChart
                          data={Object.entries(debtorSummaryQuery.data.result.month_wise_profit)
                            .map(([yearMonth, el]) => {
                              return {
                                year_month: yearMonth,
                                profit_without_mca: el.profit_without_mca,
                                profit_with_mca: el.profit_with_mca,
                              };
                            })
                            .sort(
                              (a, b) => Number(a.year_month?.replace("-", "")) - Number(b.year_month?.replace("-", "")),
                            )}
                          labelKey="year_month"
                          value1Key={"profit_with_mca"}
                          value2Key={"profit_without_mca"}
                        />
                      </div>
                    ) : null}
                  </AppCard>
                )}
                {debtorSummaryQuery.isError ? null : (
                  <AppCard>
                    <Typography.Title level={5} className="!m-0">
                      Weekly Revenue Distribution
                    </Typography.Title>
                    <div style={{ width: "100%", height: 300 }}>
                      <WeeklyDistributionPieChart data={weeklyCreditPie} dataKey="value" />
                    </div>
                  </AppCard>
                )}
                {debtorSummaryQuery.isError ? null : (
                  <AppCard>
                    <Typography.Title level={5} className="!m-0">
                      Withdrawal Percentage
                    </Typography.Title>
                    <div style={{ width: "100%", height: 300 }}>
                      <WithdrawalPercentageBarChart withdrawal_percentage={withdrawal_percentage} />
                    </div>
                  </AppCard>
                )}
                {debtorSummaryQuery.isError ? null : (
                  <AppCard>
                    <Typography.Title level={5} className="!m-0">
                      Total Negative Cashflow Days
                    </Typography.Title>
                    <div style={{ width: "100%", height: 300 }}>
                      {debtorSummaryQuery?.data?.result?.negative_days ? (
                        <ReNegativeDays
                          data={debtorSummaryQuery?.data?.result?.negative_days}
                          labelKey="year_month"
                          valueKey={"count"}
                        />
                      ) : (
                        loader
                      )}
                    </div>
                  </AppCard>
                )}
                {debtorSummaryQuery.isError ? null : (
                  <AppCard>
                    <Typography.Title level={5} className="!m-0">
                      Number of NSF Transactions
                    </Typography.Title>
                    <div style={{ width: "100%", height: 300 }}>
                      {debtorSummaryQuery?.data?.result?.nsf_transactions ? (
                        <ReNsfTransactions
                          data={debtorSummaryQuery?.data?.result?.nsf_transactions}
                          labelKey="year_month"
                          valueKey={"count"}
                        />
                      ) : (
                        loader
                      )}
                    </div>
                  </AppCard>
                )}
                {/* <MortgageCalculator application={application} /> */}
                {debtorSummaryQuery.isError ? null : (
                  <AppCard className="col-span-2">
                    <Typography.Title level={5} className="!m-0">
                      Balance v/s True Balance
                    </Typography.Title>
                    <div style={{ height: 400, width: "100%" }}>
                      {debtorSummaryQuery?.data?.result?.balance_comparison ? (
                        <StackedLineChart
                          data={debtorSummaryQuery?.data?.result?.balance_comparison}
                          valueKeys={["balance", "true_balance"]}
                          labelKey="date"
                        />
                      ) : (
                        loader
                      )}
                    </div>
                  </AppCard>
                )}
                {debtorSummaryQuery.isError ? null : debtorSummaryQuery?.data?.result?.creditor_monthly_withhold &&
                  debtorSummaryQuery.data.result.creditor_monthly_withhold.length !== 0 ? (
                  <AppCard key={`creditor_monthly_withhold-`} className="col-span-2">
                    <div style={{ width: "100%" }}>
                      <MultiCreditorMonthlyWithhold data={debtorSummaryQuery.data.result.creditor_monthly_withhold} />
                    </div>
                  </AppCard>
                ) : null}
                {/* {debtorSummaryQuery.isError ? null : debtorSummaryQuery?.data?.result?.creditor_monthly_withhold &&
                  debtorSummaryQuery.data.result.creditor_monthly_withhold.length !== 0 ? (
                  <AppCard className="col-span-2">
                    <Typography.Title level={5} className="!mb-4">
                      {"Creditor Monthly Withhold"}
                    </Typography.Title>
                    <div style={{ width: "100%" }}>
                      <MonthlyBreakdownTable data={debtorSummaryQuery?.data?.result?.creditor_monthly_withhold} />
                    </div>
                  </AppCard>
                ) : null} */}
              </div>
            </div>
          );
        }}
      />
    </div>
  );
}

function CategorisedCreditTransactions({
  debtorSummary,
  appKey,
}: {
  debtorSummary: T_QueryData<typeof ApiService.onboarding.useGetDebtorSummary>["result"];
  appKey?: string;
}) {
  const fullApplication = ApiService.application.useGetByAppKey(appKey);
  const queryClient = useQueryClient();
  const applicationData = fullApplication.data?.application;
  const categorisedCreditTransactions = debtorSummary?.categorised_credit_transactions;
  const categorisedCreditTransactionList = debtorSummary?.categorised_credit_transaction_list;
  const availableMonths = Object.keys(categorisedCreditTransactions)?.sort((a, b) => {
    return Number(a?.replace("-", "")) - Number(b?.replace("-", ""));
  });
  const [selectedCategory, setSelectedCategory] = useState<{ category: string; monthYear: string } | null>(null);
  const [protectedReceivables, setProtectedReceivables] = useState<string[]>(
    applicationData?.protected_recievables ?? [],
  );
  const [protectedReceivablesFromApi, setProtectedReceivablesFromApi] = useState<string[]>(
    applicationData?.protected_recievables ?? [],
  );
  useEffect(() => {
    if (applicationData?.protected_recievables) {
      setProtectedReceivablesFromApi(applicationData.protected_recievables);
      setProtectedReceivables(applicationData.protected_recievables);
    }
  }, [applicationData?.protected_recievables]);

  const availableCategories =
    categorisedCreditTransactions && Object.values(categorisedCreditTransactions)?.length > 0
      ? Object.keys(Object.values(categorisedCreditTransactions)?.[0])
      : undefined;
  const categoryColors = availableCategories
    ? Object.fromEntries(
        availableCategories?.map((el, i) => {
          return [el, CHART_COLORS[i]];
        }),
      )
    : undefined;

  const [selectedMonths, setSelectedMonths] = useState<string[]>([availableMonths[0]]);
  const updateProtectedReceivales = ApiService.application.useUpdateProtectedReceivbles();

  const filteredCategoryTransactionList = categorisedCreditTransactionList?.filter(
    (cat) => cat.category === selectedCategory?.category && cat.year_month === selectedCategory.monthYear,
  );

  // const sortedSelectedData = Object.entries(
  //   selectedMonth && selectedMonth in categorisedCreditTransactions
  //     ? categorisedCreditTransactions?.[selectedMonth]
  //     : categorisedCreditTransactions?.[availableMonths[0]],
  // )
  //   .map(([category, data]) => {
  //     return { category: category, ...data };
  //   })
  //   .sort((a, b) => b.amount - a.amount);
  // const thisMonthTotal = sortedSelectedData?.reduce(
  //   (prev, curr) => {
  //     return { amount: prev.amount + curr.amount, count: prev.count + curr.count };
  //   },
  //   { amount: 0, count: 0 },
  // );
  const highestMonthAmount = Math.max(
    ...Object.entries(categorisedCreditTransactions).map(([month, categoryData]) => {
      return Object.values(categoryData).reduce((prev, curr) => prev + curr.amount, 0);
    }),
  );
  if (!availableCategories) {
    return null;
  }

  return (
    <AppCard className="transition-[height] col-span-2">
      {selectedCategory === null ? (
        <div>
          <div className="flex gap-3 mb-3 justify-between items-center">
            <Typography.Title level={5} className="!m-0">
              Revenue Source
            </Typography.Title>
            <div className="flex items-center gap-2">
              {protectedReceivables.length > 0 &&
                JSON.stringify(protectedReceivables.sort()) !== JSON.stringify(protectedReceivablesFromApi.sort()) && (
                  <Button
                    loading={updateProtectedReceivales.isPending}
                    onClick={() => {
                      updateProtectedReceivales.mutate(
                        {
                          protected_recievables: protectedReceivables,
                          appkey: appKey ?? "",
                        },
                        {
                          onSuccess: () => {
                            message.success("Protected receivables updated successfully");
                            // setProtectedReceivables([]);
                            queryClient.invalidateQueries({ queryKey: ["application", appKey] });
                          },
                        },
                      );
                    }}
                    variant="outlined"
                    color="purple"
                  >
                    Update Category ({protectedReceivables.length})
                  </Button>
                )}
              {availableMonths ? (
                <Select
                  style={{ width: 150 }}
                  mode="multiple"
                  maxTagCount={"responsive"}
                  value={selectedMonths}
                  onChange={(value) => {
                    setSelectedMonths(
                      (value && value?.length > 0 ? value : selectedMonths)?.sort((a, b) => {
                        return Number(a?.replace("-", "")) - Number(b?.replace("-", ""));
                      }),
                    );
                  }}
                  options={availableMonths?.map((el) => ({
                    label: dayjs(el, "YYYY-MM").format("MMM YYYY"),
                    value: el,
                  }))}
                />
              ) : (
                <div />
              )}
            </div>
          </div>
          <Divider />

          <div style={{ width: "100%" }} className="flex flex-col gap-3">
            {availableCategories?.map((category) => {
              return (
                <div key={category} className="flex flex-col gap-2">
                  <div className="flex items-center gap-1">
                    <Tooltip title="Mark as receivables protected">
                      <Checkbox
                        checked={protectedReceivables.includes(category)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setProtectedReceivables((prev) => [...prev, category]);
                          } else {
                            setProtectedReceivables((prev) => prev.filter((item) => item !== category));
                          }
                        }}
                      />
                    </Tooltip>
                    <Typography.Text style={{ margin: 0 }} className="text-sm font-semibold">
                      {category}
                    </Typography.Text>
                  </div>
                  {selectedMonths?.map((monthYear) => {
                    const data = categorisedCreditTransactions?.[monthYear]?.[category];
                    return (
                      <Fragment key={monthYear}>
                        <div
                          className="flex flex-col gap-2 cursor-pointer"
                          onClick={() => setSelectedCategory({ category, monthYear })}
                        >
                          <div className="grid gap-1" style={{ gridTemplateColumns: "100px 1fr 1fr" }}>
                            <Typography.Text className="text-xs" type="secondary">
                              {dayjs(monthYear, "YYYY-MM").format("MMM YYYY")}
                            </Typography.Text>
                            <div className="flex justify-between gap-2 items-center">
                              <div className="inline-flex gap-1 items-center">
                                <Typography.Text className="text-xs font-bold py-0.5 px-1 bg-gray-100 rounded-sm">
                                  {data.count}
                                </Typography.Text>
                                <Typography.Text strong className="text-sm">
                                  {formatMoney(data.amount)}
                                </Typography.Text>
                                <Typography.Text className="text-sm font-bold text-green-600">
                                  {formatPercent(data.percentage)}
                                </Typography.Text>
                              </div>
                            </div>
                            <div
                              className="flex-1 min-h-5 transition-[width] duration-300"
                              style={{
                                width: `calc(100% * ${data.amount / highestMonthAmount})`,
                                backgroundColor: categoryColors?.[category],
                                minWidth: 1,
                              }}
                            />
                          </div>
                        </div>
                      </Fragment>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      ) : filteredCategoryTransactionList ? (
        <CreditTransactionsList
          creditTransactionList={filteredCategoryTransactionList}
          setSelectedCategory={setSelectedCategory}
          selectedCategory={selectedCategory}
        />
      ) : null}
    </AppCard>
  );
}

function CreditTransactionsList({
  creditTransactionList,
  setSelectedCategory,
  selectedCategory,
}: {
  creditTransactionList: { date: string; description: string; amount: string; category: string; year_month: string }[];
  setSelectedCategory: React.Dispatch<
    React.SetStateAction<{
      category: string;
      monthYear: string;
    } | null>
  >;
  selectedCategory: {
    category: string;
    monthYear: string;
  } | null;
}) {
  const columns: ColumnsType<{
    date: string;
    description: string;
    amount: string;
    category: string;
    year_month: string;
  }> = [
    {
      key: "date",
      title: "Date",
      render: (_, record) => {
        return <Typography.Text>{formatDate(record.date)}</Typography.Text>;
      },
    },
    {
      key: "amount",
      title: "Amount",
      render: (_, record) => {
        return <Typography.Text>{formatMoney(Number(record.amount) ?? 0)}</Typography.Text>;
      },
    },
    {
      key: "description",
      title: "Description",
      render: (_, record) => {
        return <Typography.Text>{record.description}</Typography.Text>;
      },
    },
  ];

  const year = selectedCategory?.monthYear.split("-")[0];
  const month = dayjs()
    .month(Number(selectedCategory?.monthYear.split("-")[1]) - 1)
    .format("MMMM");
  const category = selectedCategory?.category;

  return (
    <div>
      <div className="flex gap-4 items-center mb-4">
        <IoArrowBack className="cursor-pointer" onClick={() => setSelectedCategory(null)} />
        <Typography.Title level={5} className="!m-0">
          {month}, {year} - {category} - Transaction List
        </Typography.Title>
      </div>
      <Table
        scroll={{ x: "max-content" }}
        virtual={false}
        size="small"
        columns={columns}
        dataSource={creditTransactionList}
      />
    </div>
  );
}

export function WalletTopupModal({
  open,
  onClose,
  applicationId,
}: {
  open: boolean;
  onClose: () => void;
  applicationId: string;
}) {
  const [form] = Form.useForm();
  const topup = ApiService.application.useTopupApplicationWallet();

  const [valid, setValid] = useState(false);

  const handleSubmit = (values: any, force_create?: "true") => {
    const { budget, ref_transaction_id, posting_date } = values;

    topup.mutate(
      {
        application_id: applicationId,
        budget,
        ref_transaction_id,
        posting_date: posting_date ? ((posting_date as any).toDate?.() ?? new Date(posting_date)) : undefined,
        force_create,
      },
      {
        onSuccess: (res) => {
          message.success(res.message);

          queryClient.invalidateQueries({
            queryKey: ["application", "wallet/balance", applicationId],
          });

          form.resetFields();
          setValid(false);
          onClose();
        },
        onError: (err: any) => {
          const status = err?.response?.status;

          if (status === 404 && !force_create) {
            Modal.confirm({
              title: "Wallet not found",
              content: "No wallet exists. Create one and retry?",
              okText: "Create & Retry",
              cancelText: "Cancel",
              onOk: () => handleSubmit(values, "true"),
            });
            return;
          }

          // message.error(err?.response?.data?.message || "Something went wrong");
        },
      },
    );
  };

  return (
    <Modal
      title="Topup Wallet"
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Topup"
      centered
      width={520}
      okButtonProps={{
        disabled: !valid || topup.isPending,
        loading: topup.isPending,
      }}
      destroyOnHidden={false}
      afterOpenChange={(o) => {
        if (!o) {
          form.resetFields();
          setValid(false);
        }
      }}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={(values) => handleSubmit(values)}
        onValuesChange={() => {
          const val = form.getFieldValue("budget");
          setValid(typeof val === "number" && val >= 1);
        }}
      >
        <div className="p-4 border rounded-xl bg-amber-50 space-y-4">
          <div className="text-sm font-medium text-gray-700">Wallet Topup</div>

          <Form.Item
            name="budget"
            label={<span className="text-xs text-gray-500">Amount to add to wallet</span>}
            rules={[
              { required: true, message: "Please enter amount." },
              { type: "number", min: 1, message: "Minimum $1." },
            ]}
          >
            <InputNumber
              size="large"
              style={{ width: "100%" }}
              step={0.01}
              precision={2}
              className="rounded-lg"
              placeholder="Enter amount"
            />
          </Form.Item>
        </div>

        {/* Optional */}
        <div className="mt-4 p-4 border rounded-xl bg-gray-50 space-y-4">
          <div className="text-sm font-medium text-gray-700">Transaction Details (optional)</div>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="ref_transaction_id"
              label={<span className="text-xs text-gray-500">Reference ID</span>}
              className="mb-0"
            >
              <Input placeholder="Enter transaction reference ID" className="rounded-lg" />
            </Form.Item>

            <Form.Item
              name="posting_date"
              label={<span className="text-xs text-gray-500">Posting Date</span>}
              className="mb-0"
            >
              <DatePicker style={{ width: "100%" }} className="rounded-lg" />
            </Form.Item>
          </div>
        </div>
      </Form>
    </Modal>
  );
}

export function WalletTopupWidget({ applicationId }: { applicationId: string }) {
  const [open, setOpen] = useState(false);

  const { data, isLoading } = ApiService.application.useGetWalletBalance(applicationId);

  const balance = Number(data?.result?.balance ?? 0);

  return (
    <>
      <div className="flex items-center justify-between p-4 rounded-xl border bg-green-50 shadow-sm">
        <div>
          <div className="text-xs text-green-700">Wallet</div>

          {isLoading ? (
            <Spin size="small" />
          ) : (
            <div className="text-lg font-semibold text-green-600">${balance.toFixed(2)}</div>
          )}
        </div>

        <Button type="primary" className="bg-green-600 hover:bg-green-700" onClick={() => setOpen(true)}>
          Topup
        </Button>
      </div>

      <WalletTopupModal applicationId={applicationId} open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export function EditableLedgerModal({
  applicationId,
  appKey,
  loans,
  open,
  onClose,
}: {
  applicationId: string | undefined;
  appKey: string | undefined;
  loans?: T_Populate<LoanSchemaDto, { attachments: FileSchemaDto[] }>[];
  open: boolean;
  onClose: () => void;
}) {
  const EMPTY_ROW_TEMPLATE: LedgerSchemaDto = {
    week: 0,
    category: LEDGER_CATEGORY.INITIAL,
    amount: 0,
    distribution: { program_fee: 0, service_fee: 0, legal_fee: 0, creditor_total: 0 },
    creditors: (loans ?? []).map((l) => ({
      creditor_id: l._id,
      amount: 0,
      paid: 0,
      status: DISBURSEMENT_STATUS.NONE,
      creditor_name: l.creditor_name,
    })),
  } as LedgerSchemaDto;

  const paymentTable = ApiService.application.useGetPaymentTable(applicationId);
  const bulkUpdate = ApiService.application.useBulkUpdateLedger();

  const [ledgerType, setLedgerType] = useState<LEDGER_CATEGORY>(LEDGER_CATEGORY.WEEKLY);
  const [fullLedgerLoaded, setFullLedgerLoaded] = useState(false);
  const [editableRows, setEditableRows] = useState<LedgerSchemaDto[]>([]);
  const [newRowIds, setNewRowIds] = useState<Set<string>>(new Set());
  const [repeatCount, setRepeatCount] = useState<number>(1);
  const [repeatPopoverOpen, setRepeatPopoverOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [copyCount, setCopyCount] = useState<number>(1);
  const [copyPopoverOpen, setCopyPopoverOpen] = useState(false);
  const [resetPopoverOpen, setResetPopoverOpen] = useState(false);

  const isMonthly = ledgerType === LEDGER_CATEGORY.MONTHLY;

  // Single-level undo: snapshot taken before any structural mutation
  // (delete row, bulk delete, insert, add blank rows, copy rows, load existing, reset, switch type).
  const [lastSnapshot, setLastSnapshot] = useState<{
    editableRows: LedgerSchemaDto[];
    newRowIds: Set<string>;
    ledgerType: LEDGER_CATEGORY;
    fullLedgerLoaded: boolean;
    label: string;
  } | null>(null);

  const snapshotBeforeChange = (label: string) => {
    setLastSnapshot({
      editableRows: editableRows.map((r) => ({ ...r })),
      newRowIds: new Set(newRowIds),
      ledgerType,
      fullLedgerLoaded,
      label,
    });
  };

  const handleUndo = () => {
    if (!lastSnapshot) return;
    setEditableRows(lastSnapshot.editableRows);
    setNewRowIds(lastSnapshot.newRowIds);
    setLedgerType(lastSnapshot.ledgerType);
    setFullLedgerLoaded(lastSnapshot.fullLedgerLoaded);
    setLastSnapshot(null);
  };

  const blankRow = (template: LedgerSchemaDto): LedgerSchemaDto => ({
    ...template,
    amount: 0,
    distribution: {
      ...template.distribution,
      program_fee: 0,
      service_fee: 0,
      legal_fee: 0,
      creditor_total: 0,
    },
    creditors: (template.creditors ?? []).map((c) => ({ ...c, amount: 0 })),
    posting_date: undefined,
    payment_date: undefined,
    override_week_day_idx: undefined,
  });

  // Renumbers weeks sequentially based on array position, keeping the Initial (week 0)
  // row — always first — untouched. Used after any insert/delete/copy so the sequence
  // stays contiguous regardless of how many rows moved.
  const renumberWeeks = (rows: LedgerSchemaDto[]): LedgerSchemaDto[] => {
    let nextWeek = rows[0]?.week === 0 ? 1 : 0;
    return rows.map((r, idx) => {
      if (idx === 0 && r.week === 0) return r;
      const updated = { ...r, week: nextWeek };
      nextWeek++;
      return updated;
    });
  };

  const detectLedgerType = (rows: LedgerSchemaDto[]): LEDGER_CATEGORY =>
    rows.length > 4 && rows[4]?.category === LEDGER_CATEGORY.MONTHLY ? LEDGER_CATEGORY.MONTHLY : LEDGER_CATEGORY.WEEKLY;

  useEffect(() => {
    if (open && paymentTable.data?.payment_table) {
      if (paymentTable.data.payment_table.length > 0) {
        setEditableRows(paymentTable.data.payment_table.map((r) => ({ ...r })));
        setNewRowIds(new Set());
        setFullLedgerLoaded(true);
        setLedgerType(detectLedgerType(paymentTable.data.payment_table));
      } else {
        const tempId = `new-${Date.now()}-initial`;
        setEditableRows([
          {
            ...blankRow(editableRows[0] ?? EMPTY_ROW_TEMPLATE),
            _id: tempId as any,
            week: 0,
            category: LEDGER_CATEGORY.INITIAL,
          },
        ]);
        setNewRowIds(new Set([tempId]));
        setFullLedgerLoaded(false);
        setLedgerType(LEDGER_CATEGORY.WEEKLY);
      }
      setLastSnapshot(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, paymentTable.data]);

  const handleLoadExisting = () => {
    if (!paymentTable.data?.payment_table?.length) return;
    snapshotBeforeChange("Loaded existing ledger");
    setEditableRows(paymentTable.data.payment_table.map((r) => ({ ...r })));
    setFullLedgerLoaded(true);
    setLedgerType(detectLedgerType(paymentTable.data.payment_table));
    setNewRowIds(new Set());
    setSelectedRowKeys([]);
    setResetPopoverOpen(false);
  };

  const handleResetToBlank = () => {
    snapshotBeforeChange("Ledger was reset");
    const tempId = `new-${Date.now()}-initial`;
    setEditableRows([
      {
        ...blankRow(editableRows[0] ?? EMPTY_ROW_TEMPLATE),
        _id: tempId as any,
        week: 0,
        category: LEDGER_CATEGORY.INITIAL,
      },
    ]);
    setNewRowIds(new Set([tempId]));
    setFullLedgerLoaded(false);
    setSelectedRowKeys([]);
    setResetPopoverOpen(false);
  };

  // Switching type is destructive: no attempt at conversion, always a clean single
  // Initial row in the new type. Protected by the undo banner, same as every other
  // structural change.
  const handleSwitchLedgerType = (newType: LEDGER_CATEGORY) => {
    if (newType === ledgerType) return;
    snapshotBeforeChange(`Switched to ${newType} ledger`);
    const tempId = `new-${Date.now()}-initial`;
    setEditableRows([
      {
        ...blankRow(editableRows[0] ?? EMPTY_ROW_TEMPLATE),
        _id: tempId as any,
        week: 0,
        category: LEDGER_CATEGORY.INITIAL,
      },
    ]);
    setNewRowIds(new Set([tempId]));
    setFullLedgerLoaded(false);
    setLedgerType(newType);
    setSelectedRowKeys([]);
  };

  // All update functions use the row's _id so they work correctly with row selection/reordering.
  const updateRowById = (id: string, patch: Partial<LedgerSchemaDto>) => {
    setEditableRows((prev) => prev.map((r) => (String(r._id) === id ? { ...r, ...patch } : r)));
  };

  const updateRowDistributionById = (
    id: string,
    field: Exclude<keyof LedgerSchemaDto["distribution"], "creditor_total">,
    value: number,
  ) => {
    setEditableRows((prev) =>
      prev.map((r) => {
        if (String(r._id) !== id) return r;
        const newDist = { ...r.distribution, [field]: value };
        const creditorSum = (r.creditors ?? []).reduce((sum, c) => sum + (c.amount ?? 0), 0);
        const amount =
          (newDist.legal_fee ?? 0) +
          (newDist.service_fee ?? 0) +
          (newDist.program_fee ?? 0) +
          Math.max(newDist.creditor_total ?? 0, creditorSum);
        return { ...r, distribution: newDist, amount };
      }),
    );
  };

  const updateRowCreditorTotalById = (id: string, value: number | null) => {
    setEditableRows((prev) =>
      prev.map((r) => {
        if (String(r._id) !== id) return r;
        const newValue = value ?? 0;
        const newDist = { ...r.distribution, creditor_total: newValue };
        const creditorSum = (r.creditors ?? []).reduce((sum, c) => sum + (c.amount ?? 0), 0);
        const amount =
          (newDist.legal_fee ?? 0) +
          (newDist.service_fee ?? 0) +
          (newDist.program_fee ?? 0) +
          Math.max(newValue, creditorSum);
        return { ...r, distribution: newDist, amount };
      }),
    );
  };

  const updateRowCreditorById = (id: string, creditorId: string, amount: number) => {
    setEditableRows((prev) =>
      prev.map((r) => {
        if (String(r._id) !== id) return r;
        const existing = (r.creditors ?? []).some((c) => String(c.creditor_id) === creditorId);
        const newCreditors = existing
          ? (r.creditors ?? []).map((c) => (String(c.creditor_id) === creditorId ? { ...c, amount } : c))
          : [
              ...(r.creditors ?? []),
              { creditor_id: creditorId as any, amount, paid: 0, status: "unpaid" as any, creditor_name: "" },
            ];
        const creditorSum = newCreditors.reduce((sum, c) => sum + (c.amount ?? 0), 0);
        const newAmount =
          (r.distribution.legal_fee ?? 0) +
          (r.distribution.service_fee ?? 0) +
          (r.distribution.program_fee ?? 0) +
          Math.max(r.distribution.creditor_total ?? 0, creditorSum);
        return { ...r, creditors: newCreditors, amount: newAmount };
      }),
    );
  };

  const handleAddRepeatRows = () => {
    if (!editableRows.length) return;
    snapshotBeforeChange("Added blank rows");
    const lastRow = editableRows[editableRows.length - 1];
    const newRows: LedgerSchemaDto[] = [];

    if (isMonthly) {
      for (let m = 0; m < repeatCount; m++) {
        for (let s = 0; s < 3; s++) {
          const tempId = `new-${Date.now()}-${m}-skip-${s}`;
          newRows.push({ ...blankRow(lastRow), _id: tempId as any, category: LEDGER_CATEGORY.SKIP });
        }
        const tempId = `new-${Date.now()}-${m}-monthly`;
        newRows.push({ ...blankRow(lastRow), _id: tempId as any, category: LEDGER_CATEGORY.MONTHLY });
      }
    } else {
      for (let i = 0; i < repeatCount; i++) {
        const tempId = `new-${Date.now()}-${i}`;
        newRows.push({ ...blankRow(lastRow), _id: tempId as any, category: LEDGER_CATEGORY.WEEKLY });
      }
    }

    setEditableRows((prev) => renumberWeeks([...prev, ...newRows]));
    setNewRowIds((prev) => {
      const next = new Set(prev);
      newRows.forEach((r) => next.add(String(r._id)));
      return next;
    });
    setRepeatPopoverOpen(false);
    setRepeatCount(1);
  };

  const handleCopySelectedRows = () => {
    if (!selectedRowKeys.length) return;
    // preserve table order, not selection-click order
    const orderedSelected = editableRows.filter((r) => selectedRowKeys.includes(String(r._id)));
    if (!orderedSelected.length) return;

    snapshotBeforeChange("Copied selected rows");

    const newRows: LedgerSchemaDto[] = [];

    for (let n = 0; n < copyCount; n++) {
      for (const row of orderedSelected) {
        if (isMonthly && row.category === LEDGER_CATEGORY.MONTHLY) {
          // Copying a monthly row means copying its whole block: 3 fresh skip rows + the monthly row.
          for (let s = 0; s < 3; s++) {
            const tempId = `copy-${Date.now()}-${n}-${String(row._id)}-skip-${s}`;
            newRows.push({
              ...row,
              _id: tempId as any,
              category: LEDGER_CATEGORY.SKIP,
              amount: 0,
              distribution: { program_fee: 0, service_fee: 0, legal_fee: 0, creditor_total: 0 },
              creditors: (row.creditors ?? []).map((c) => ({ ...c, amount: 0 })),
            });
          }
          const tempId = `copy-${Date.now()}-${n}-${String(row._id)}`;
          newRows.push({ ...row, _id: tempId as any });
        } else {
          const tempId = `copy-${Date.now()}-${n}-${String(row._id)}`;
          newRows.push({ ...row, _id: tempId as any });
        }
      }
    }

    setEditableRows((prev) => renumberWeeks([...prev, ...newRows]));
    setNewRowIds((prev) => {
      const next = new Set(prev);
      newRows.forEach((r) => next.add(String(r._id)));
      return next;
    });
    setSelectedRowKeys([]);
    setCopyPopoverOpen(false);
    setCopyCount(1);
  };

  const handleInsertRow = (id: string, position: "above" | "below") => {
    const idx = editableRows.findIndex((r) => String(r._id) === id);
    if (idx === -1) return;
    const row = editableRows[idx];
    if (row.week === 0) return;

    snapshotBeforeChange(`Inserted row ${position}`);

    const newRows: LedgerSchemaDto[] = [];

    if (isMonthly) {
      // Only offered on MONTHLY rows — inserting means inserting the whole block.
      for (let s = 0; s < 3; s++) {
        const tempId = `new-${Date.now()}-ins-skip-${s}`;
        newRows.push({ ...blankRow(row), _id: tempId as any, category: LEDGER_CATEGORY.SKIP });
      }
      const tempId = `new-${Date.now()}-ins-monthly`;
      newRows.push({ ...blankRow(row), _id: tempId as any, category: LEDGER_CATEGORY.MONTHLY });
    } else {
      const tempId = `new-${Date.now()}-ins`;
      newRows.push({ ...blankRow(row), _id: tempId as any, category: LEDGER_CATEGORY.WEEKLY });
    }

    // For monthly "above": the block must go before this row's 3 preceding skip rows
    // (the start of its own block), not right before the monthly row itself —
    // otherwise the new block's skips land in the middle of the existing block
    // and every monthly row after it drifts off a multiple of 4.
    const insertAt = isMonthly ? (position === "above" ? idx - 3 : idx + 1) : position === "above" ? idx : idx + 1;

    setEditableRows((prev) => {
      const copy = [...prev];
      copy.splice(insertAt, 0, ...newRows);
      return renumberWeeks(copy);
    });
    setNewRowIds((prev) => {
      const next = new Set(prev);
      newRows.forEach((r) => next.add(String(r._id)));
      return next;
    });
  };

  const handleDeleteRow = (id: string) => {
    const idx = editableRows.findIndex((r) => String(r._id) === id);
    if (idx === -1) return;
    if (editableRows[idx].week === 0) return;

    const removeIdxs = [idx];
    if (isMonthly && editableRows[idx].category === LEDGER_CATEGORY.MONTHLY) {
      let i = idx - 1;
      while (i >= 0 && editableRows[i].category === LEDGER_CATEGORY.SKIP) {
        removeIdxs.push(i);
        i--;
      }
    }
    const removeSet = new Set(removeIdxs);
    const removedIds = editableRows.filter((_, i2) => removeSet.has(i2)).map((r) => String(r._id));

    snapshotBeforeChange("Row was deleted");

    setEditableRows((prev) => renumberWeeks(prev.filter((_, i2) => !removeSet.has(i2))));
    setNewRowIds((prev) => {
      const next = new Set(prev);
      removedIds.forEach((rid) => next.delete(rid));
      return next;
    });
    setSelectedRowKeys((prev) => prev.filter((k) => !removedIds.includes(k)));
  };

  const handleBulkDeleteRows = () => {
    if (!selectedRowKeys.length) return;

    const removeIdxSet = new Set<number>();
    editableRows.forEach((r, idx) => {
      if (r.week === 0) return;
      if (!selectedRowKeys.includes(String(r._id))) return;
      removeIdxSet.add(idx);
      if (isMonthly && r.category === LEDGER_CATEGORY.MONTHLY) {
        let i = idx - 1;
        while (i >= 0 && editableRows[i].category === LEDGER_CATEGORY.SKIP) {
          removeIdxSet.add(i);
          i--;
        }
      }
    });
    if (!removeIdxSet.size) return;

    const removedIds = editableRows.filter((_, idx) => removeIdxSet.has(idx)).map((r) => String(r._id));

    snapshotBeforeChange("Deleted selected rows");

    setEditableRows((prev) => renumberWeeks(prev.filter((_, idx) => !removeIdxSet.has(idx))));
    setNewRowIds((prev) => {
      const next = new Set(prev);
      removedIds.forEach((id) => next.delete(id));
      return next;
    });
    setSelectedRowKeys([]);
  };

  const handleUpdate = () => {
    if (!appKey) return;

    const zeroRows = editableRows.filter((row) => row.category !== LEDGER_CATEGORY.SKIP && row.amount === 0);
    if (zeroRows.length) {
      message.error(`${zeroRows.length} row(s) have a zero total. Amount must be greater than 0.`);
      return;
    }

    const rows = editableRows.map((row) => {
      const totalCreditorAmount = (row.creditors ?? []).reduce((sum, c) => sum + c.amount, 0);
      const finalCreditorTotal = Math.max(row.distribution?.creditor_total ?? 0, totalCreditorAmount);
      return {
        _id: newRowIds.has(String(row._id)) ? undefined : String(row._id),
        week: row.week,
        override_week_day_idx: row.override_week_day_idx,
        amount: row.amount,
        category: row.category,
        distribution: {
          program_fee: row.distribution?.program_fee ?? 0,
          service_fee: row.distribution?.service_fee ?? 0,
          legal_fee: row.distribution?.legal_fee ?? 0,
          creditor_total: finalCreditorTotal,
          final_creditor_total: finalCreditorTotal,
        },
        creditors: (row.creditors ?? []).map((c) => ({
          creditor_name: loans?.find((l) => l._id === String(c.creditor_id))?.creditor_name ?? "",
          creditor_id: String(c.creditor_id),
          amount: c.amount,
        })),
        posting_date: row.posting_date,
        payment_date: row.payment_date,
        status: row.status,
        isNew: newRowIds.has(String(row._id)),
      };
    });

    bulkUpdate.mutate(
      { appKey, rows },
      {
        onSuccess: () => {
          message.success("Ledger updated successfully");
          onClose();
          paymentTable.refetch();
        },
      },
    );
  };

  const moneyFormatter = (value: any) => {
    const [start, end] = `${value ?? 0}`.split(".");
    const v = `${start}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return end ? `${v}.${end}` : v;
  };
  const moneyParser = (value: any) => value?.replace(/\$\s?|(,*)/g, "") as unknown as number;

  // Monthly mode hides skip rows from view — they're implementation detail, still
  // included in editableRows/payload/summary, just not rendered or selectable.
  const displayRows = isMonthly ? editableRows.filter((r) => r.category !== LEDGER_CATEGORY.SKIP) : editableRows;

  const editableColumns: TableProps<LedgerSchemaDto>["columns"] = [
    {
      key: "week",
      dataIndex: "week",
      title: isMonthly ? "Month" : "Week",
      fixed: "left",
      width: 90,
      render: (val) => {
        if (val === 0) return "Initial";
        if (isMonthly) return `Month ${val / 4}`;
        return val;
      },
    },
    {
      key: "override_week_day_idx",
      title: "Day of week for payment",
      align: "center",
      width: 110,
      render: (_val, record) => (
        <Select
          allowClear
          placeholder="Day"
          style={{ width: "100%" }}
          value={record.override_week_day_idx}
          onChange={(v) => updateRowById(String(record._id), { override_week_day_idx: v })}
          options={DAY_OPTIONS}
        />
      ),
    },
    {
      key: "amount",
      dataIndex: "amount",
      title: "Total Sum (autocalculated)",
      align: "right",
      width: 140,
      render: (_val, record) => (
        <div>
          <InputNumber
            readOnly
            controls={false}
            prefix="$"
            value={record.amount}
            formatter={moneyFormatter}
            parser={moneyParser}
            style={{ width: "100%" }}
            disabled
            status={record.amount === 0 ? "warning" : undefined}
          />
          {record.amount === 0 && (
            <Typography.Text type="warning" style={{ fontSize: 11 }}>
              Zero total
            </Typography.Text>
          )}
        </div>
      ),
    },
    {
      key: "program_fee",
      title: "Program Fee",
      align: "right",
      width: 140,
      render: (_val, record) => (
        <InputNumber
          controls={false}
          min={0}
          prefix="$"
          value={record.distribution?.program_fee}
          onChange={(v) => updateRowDistributionById(String(record._id), "program_fee", (v as number) ?? 0)}
          formatter={moneyFormatter}
          parser={moneyParser}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      key: "service_fee",
      title: "Service Fee",
      align: "right",
      width: 140,
      render: (_val, record) => (
        <InputNumber
          controls={false}
          min={0}
          prefix="$"
          value={record.distribution?.service_fee}
          onChange={(v) => updateRowDistributionById(String(record._id), "service_fee", (v as number) ?? 0)}
          formatter={moneyFormatter}
          parser={moneyParser}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      key: "legal_fee",
      title: "Legal Fee",
      align: "right",
      width: 140,
      render: (_val, record) => (
        <InputNumber
          controls={false}
          min={0}
          prefix="$"
          value={record.distribution?.legal_fee}
          onChange={(v) => updateRowDistributionById(String(record._id), "legal_fee", (v as number) ?? 0)}
          formatter={moneyFormatter}
          parser={moneyParser}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      key: "creditor_total",
      title: (
        <span className="inline-flex items-center gap-1">
          Creditor Total
          <Tooltip title="Money parked for creditors beyond the itemized breakdown. The actual amount counted toward payment is whichever is higher: this value, or the sum of the individual creditor amounts.">
            <TbInfoCircle className="cursor-help" />
          </Tooltip>
        </span>
      ),
      align: "right",
      width: 170,
      render: (_val, record) => {
        const creditorSum = (record.creditors ?? []).reduce((sum, c) => sum + (c.amount ?? 0), 0);
        const effective = Math.max(record.distribution?.creditor_total ?? 0, creditorSum);
        return (
          <div>
            <InputNumber
              controls={false}
              min={0}
              prefix="$"
              disabled={record.week === 0}
              value={record.distribution?.creditor_total}
              onChange={(v) => updateRowCreditorTotalById(String(record._id), v as number | null)}
              formatter={moneyFormatter}
              parser={moneyParser}
              style={{ width: "100%" }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              Final value to be set: ${moneyFormatter(effective)}
            </Typography.Text>
          </div>
        );
      },
    },
    {
      key: "creditors",
      dataIndex: "creditor",
      title: "Creditor Breakdown",
      align: "center" as const,
      width: (loans ?? []).length * 140,
      children: (loans ?? []).map((loan) => ({
        key: `creditor-${loan._id}`,
        title: loan.creditor_name,
        align: "right" as const,
        width: 140,
        render: (_val: any, record: LedgerSchemaDto) => {
          const thisCreditor = record.creditors?.find((c) => String(c.creditor_id) === loan._id);
          return (
            <InputNumber
              controls={false}
              min={0}
              prefix="$"
              disabled={record.week === 0}
              value={thisCreditor?.amount ?? 0}
              onChange={(v) => updateRowCreditorById(String(record._id), loan._id, (v as number) ?? 0)}
              formatter={moneyFormatter}
              parser={moneyParser}
              style={{ width: "100%" }}
            />
          );
        },
      })),
    },
    {
      key: "actions",
      title: "",
      fixed: "right",
      width: 90,
      render: (_val, record) => {
        if (record.week === 0) return null;
        const canInsert = !isMonthly || record.category === LEDGER_CATEGORY.MONTHLY;
        return (
          <div className="flex items-center gap-1">
            {canInsert && (
              <>
                <Tooltip title="Insert row above">
                  <Button
                    size="small"
                    type="text"
                    icon={<TbArrowUp />}
                    onClick={() => handleInsertRow(String(record._id), "above")}
                  />
                </Tooltip>
                <Tooltip title="Insert row below">
                  <Button
                    size="small"
                    type="text"
                    icon={<TbArrowDown />}
                    onClick={() => handleInsertRow(String(record._id), "below")}
                  />
                </Tooltip>
              </>
            )}
            <Button
              size="small"
              danger
              type="text"
              icon={<TbX />}
              onClick={() => handleDeleteRow(String(record._id))}
            />
          </div>
        );
      },
    },
  ];

  const creditorColumnsCount = loans?.length ?? 0;

  return (
    <Modal
      open={open}
      onCancel={() => {
        onClose();
        setEditableRows([]);
        setFullLedgerLoaded(false);
        setSelectedRowKeys([]);
        setLastSnapshot(null);
      }}
      title={
        <div className="flex flex-col gap-1 w-full">
          <div className="flex items-center justify-between w-[95%]">
            <Typography.Text style={{ margin: 0 }}>Edit Ledger</Typography.Text>
            <div className="flex items-center gap-2">
              <Select
                size="small"
                value={ledgerType}
                style={{ width: 130 }}
                onChange={(v) => handleSwitchLedgerType(v)}
                options={[
                  { label: "Weekly", value: LEDGER_CATEGORY.WEEKLY },
                  { label: "Monthly", value: LEDGER_CATEGORY.MONTHLY },
                ]}
              />
              {fullLedgerLoaded ? (
                <Popover
                  open={resetPopoverOpen}
                  onOpenChange={setResetPopoverOpen}
                  trigger={"click"}
                  content={
                    <div className="flex flex-col items-end">
                      <Typography.Paragraph className="!max-w-[200px]">
                        This will discard any unsaved changes and start a blank ledger.
                      </Typography.Paragraph>
                      <Button onClick={handleResetToBlank} size="small">
                        Reset Ledger
                      </Button>
                    </div>
                  }
                >
                  <Button size="small" icon={<TbRefresh />}>
                    Reset Ledger
                  </Button>
                </Popover>
              ) : (
                <Popover
                  open={resetPopoverOpen}
                  onOpenChange={setResetPopoverOpen}
                  trigger={"click"}
                  content={
                    <div className="flex flex-col items-end">
                      <Typography.Paragraph className="!max-w-[200px]">
                        This will discard any unsaved changes and reload the saved ledger.
                      </Typography.Paragraph>
                      <Button onClick={handleLoadExisting} size="small">
                        Load Ledger
                      </Button>
                    </div>
                  }
                >
                  <Button size="small" icon={<TbRefresh />}>
                    Load Existing Ledger
                  </Button>
                </Popover>
              )}
            </div>
          </div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Choose weekly or monthly ledger before making changes — switching afterward will reset this ledger.
          </Typography.Text>
          <Alert
            type="warning"
            showIcon
            closable
            message="Edit the ledger after finalising the organisation and its loans. Changing these later may reset the ledger and erase your manual edits."
            style={{ fontSize: 12, padding: "4px 8px" }}
          />
        </div>
      }
      width={1400}
      maskClosable={false}
      footer={null}
      centered
      destroyOnHidden
      styles={{ body: { padding: 0 } }}
    >
      {lastSnapshot && (
        <Alert
          className="rounded-none"
          type="info"
          showIcon
          closable
          onClose={() => setLastSnapshot(null)}
          message={
            <div className="flex w-full items-center justify-between gap-4">
              <span>{lastSnapshot.label} — undo?</span>
              <Button size="small" onClick={handleUndo}>
                Undo
              </Button>
            </div>
          }
        />
      )}
      <Table<LedgerSchemaDto>
        className="editable-ledger-table"
        virtual
        scroll={{ y: 500, x: creditorColumnsCount ? creditorColumnsCount * 140 + 1300 : 1300 }}
        rowKey={(r) => String(r._id)}
        bordered
        loading={{ spinning: paymentTable.isFetching, indicator: loaderSm }}
        columns={editableColumns}
        dataSource={displayRows}
        pagination={false}
        sticky={{ offsetHeader: 0, offsetScroll: 0 }}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys as string[]),
          columnWidth: 40,
          getCheckboxProps: (record) => ({ disabled: record.week === 0 }),
        }}
        summary={() => {
          const rows = editableRows;
          return (
            <Table.Summary fixed="bottom">
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2} className="bg-gray-100 font-bold text-gray-800">
                  Total
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right" className="bg-gray-100 font-bold text-gray-800">
                  {formatMoney(rows.reduce((p, c) => p + (c.amount ?? 0), 0))}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right" className="bg-gray-100 font-bold text-gray-800">
                  {formatMoney(rows.reduce((p, c) => p + (c.distribution?.program_fee ?? 0), 0))}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right" className="bg-gray-100 font-bold text-gray-800">
                  {formatMoney(rows.reduce((p, c) => p + (c.distribution?.service_fee ?? 0), 0))}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right" className="bg-gray-100 font-bold text-gray-800">
                  {formatMoney(rows.reduce((p, c) => p + (c.distribution?.legal_fee ?? 0), 0))}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="right" className="bg-gray-100 font-bold text-gray-800">
                  {formatMoney(rows.reduce((p, c) => p + (c.distribution?.creditor_total ?? 0), 0))}
                </Table.Summary.Cell>
                {(loans ?? []).map((loan, idx) => {
                  const total = rows.reduce((sum, row) => {
                    const creditor = row.creditors?.find((c) => String(c.creditor_id) === loan._id);
                    return sum + (creditor?.amount ?? 0);
                  }, 0);
                  return (
                    <Table.Summary.Cell
                      key={loan._id}
                      index={7 + idx}
                      align="right"
                      className="bg-gray-100 font-bold text-gray-800"
                    >
                      {total ? formatMoney(total) : "-"}
                    </Table.Summary.Cell>
                  );
                })}
                <Table.Summary.Cell index={7 + creditorColumnsCount} className="bg-gray-100" />
              </Table.Summary.Row>
            </Table.Summary>
          );
        }}
        footer={() => (
          <div className="flex justify-between items-center py-2">
            <div className="flex gap-2">
              <Popover
                open={repeatPopoverOpen}
                onOpenChange={setRepeatPopoverOpen}
                trigger="click"
                content={
                  <div className="flex flex-col gap-3" style={{ width: 220 }}>
                    <Typography.Text className="text-sm font-medium">
                      {isMonthly ? "How many months to add?" : "How many blank rows to add?"}
                    </Typography.Text>
                    <InputNumber
                      min={1}
                      max={52}
                      value={repeatCount}
                      onChange={(v) => setRepeatCount(v as number)}
                      style={{ width: "100%" }}
                    />
                    <Button type="primary" size="small" onClick={handleAddRepeatRows}>
                      Confirm
                    </Button>
                  </div>
                }
              >
                <Button icon={<TbPlus />}>{isMonthly ? "Add Blank Months" : "Add Blank Rows"}</Button>
              </Popover>
              <Popover
                open={copyPopoverOpen}
                onOpenChange={setCopyPopoverOpen}
                trigger="click"
                content={
                  <div className="flex flex-col gap-3" style={{ width: 220 }}>
                    <Typography.Text className="text-sm font-medium">
                      Repeat the {selectedRowKeys.length} selected row(s) how many times?
                    </Typography.Text>
                    <InputNumber
                      min={1}
                      max={52}
                      value={copyCount}
                      onChange={(v) => setCopyCount(v as number)}
                      style={{ width: "100%" }}
                    />
                    <Button type="primary" size="small" onClick={handleCopySelectedRows}>
                      Confirm
                    </Button>
                  </div>
                }
              >
                <Button icon={<TbCopy />} disabled={!selectedRowKeys.length}>
                  Copy Selected {selectedRowKeys.length ? `(${selectedRowKeys.length})` : ""}
                </Button>
              </Popover>
              <Button icon={<TbX />} danger disabled={!selectedRowKeys.length} onClick={handleBulkDeleteRows}>
                Delete Selected {selectedRowKeys.length ? `(${selectedRowKeys.length})` : ""}
              </Button>
            </div>
            <Button type="primary" loading={bulkUpdate.isPending} onClick={handleUpdate}>
              Update
            </Button>
          </div>
        )}
      />
    </Modal>
  );
}
